import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Trophy, Clock, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Award, Star, Crown, Gem, TrendingDown, TrendingUp, ChevronRight, ChevronDown,
  ClipboardCheck, Truck, Wrench, Info, ExternalLink, HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import api from "@/lib/api";
import { CpCritereDetailsDialog } from "@/components/bonus/CpCritereDetailsDialog";
import { AvertissementsRichBlock } from "@/components/bonus/AvertissementsRichBlock";
import { BonusDashboardGlobal } from "@/components/bonus/BonusDashboardGlobal";
import { MesBonsTravailSaison } from "@/components/bonus/MesBonsTravailSaison";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

// ─── Config ────────────────────────────────────────────────────────────────────

const PALIER_CONFIG = {
  Bronze:  { icon: Award,       color: "text-amber-600",  bg: "bg-amber-50 border-amber-200",   label: "Bronze"  },
  Argent:  { icon: Star,        color: "text-slate-500",  bg: "bg-slate-50 border-slate-200",   label: "Argent"  },
  Or:      { icon: Crown,       color: "text-yellow-500", bg: "bg-yellow-50 border-yellow-200", label: "Or"      },
  Platine: { icon: Gem,         color: "text-blue-500",   bg: "bg-blue-50 border-blue-200",     label: "Platine" },
  Aucun:   { icon: Clock,         color: "text-slate-500",  bg: "bg-slate-50 border-slate-200",   label: "Pas de palier" },
};

// 🆕 2026-02-21 Phase 2 — Refonte complète des textes d'aide (brief utilisateur)
// Tous les textes ont été remplacés par les libellés clairs, positifs et orientés progression
// rédigés par le propriétaire. Voir /app/memory/BONUS_SPEC_FINALE_2026-05-04.md pour réf.
const EXPLICATIONS = {
  heures_minimum: {
    title: "Heures travaillées dans la saison",
    objectif: "Atteindre le minimum d'heures requis pour être admissible au bonus de fin de saison.",
    texte: `Ce critère sert à reconnaître les employés qui sont présents de façon régulière pendant la saison et qui s'impliquent réellement dans l'équipe sur le long terme.

Le seuil de 600 heures représente environ une saison de travail à temps plein sur plusieurs mois. Il permet de réserver le bonus aux employés qui contribuent de manière constante à la production de l'entreprise.

Toutes les heures de travail validées peuvent compter dans ce total, incluant les heures sur les bons de travail réguliers, les bons de travail à l'heure, les bons de travail internes, la formation et les autres heures admissibles.

Il est normal que ce critère ne soit pas encore atteint en début ou en milieu de saison. Il évolue au fur et à mesure que les heures sont travaillées.`,
    scope: "terrain",
  },
  temps: {
    title: "Respect du temps prévu",
    objectif: "Atteindre le pourcentage requis de chantiers complétés dans le temps prévu.",
    texte: `Ce critère mesure si les bons de travail sont réalisés dans les heures prévues.

Pour les bons de travail avec un budget d'heures, la plateforme compare les heures budgétées avec les heures réellement poinçonnées par l'équipe.

Si le chantier est terminé dans le temps prévu, ou plus rapidement, il est considéré conforme pour ce critère.

Si le chantier dépasse les heures prévues, il peut faire diminuer le score.

Pour les bons de travail à l'heure, il n'y a pas de budget d'heures à comparer. Dès qu'un employé a travaillé sur un bon de travail à l'heure, ce bon de travail doit donc être considéré conforme pour le critère Temps.

Ce critère sert à mesurer le respect des heures prévues. Il est différent des heures économisées, qui servent plutôt à estimer le bonus potentiel.`,
    scope: "all",
  },
  service_call: {
    title: "Service après-travaux",
    objectif: "Limiter les retours chez les clients après la fin des travaux.",
    texte: `Ce critère mesure la qualité de finition avant de quitter le chantier.

Un service après-travaux est comptabilisé lorsqu'un client nous rappelle peu de temps après la fin des travaux pour une correction qui aurait normalement pu être réglée avant le départ de l'équipe.

L'objectif est de prendre le temps de faire un dernier tour avec le client, de valider les éléments à corriger et de terminer les ajustements possibles avant de quitter.

Un rappel plusieurs mois après les travaux ne compte pas automatiquement comme un service après-travaux lié à ce critère.

Plus ce critère est élevé, plus cela démontre que les chantiers sont bien terminés dès la première visite.`,
    scope: "all",
  },
  gestion_contrat: {
    title: "Gestion du contrat",
    objectif: "Assurer une bonne gestion du chantier et des demandes liées au contrat.",
    texte: `Ce critère mesure si les informations importantes du chantier sont bien suivies et transmises dans la plateforme.

Pour les chefs d'équipe, cela inclut notamment les commandes ou demandes qui doivent être envoyées à temps afin d'éviter des retards dans la production.

Une commande passée après l'heure limite peut diminuer ce score, car elle complique la planification et la préparation du chantier.

L'objectif est que les informations importantes soient communiquées rapidement, clairement et au bon endroit.`,
    scope: "all",
  },
  proprete: {
    title: "Propreté des unités",
    objectif: "Maintenir les unités propres, organisées et prêtes à travailler.",
    texte: `Ce critère est lié aux inspections de rangement et de propreté des unités.

Il vise à s'assurer que le matériel de l'entreprise est bien entretenu, que l'espace de travail reste fonctionnel et que chaque équipe peut commencer ses journées efficacement.

Les inspections peuvent tenir compte notamment de :
• la propreté générale de l'unité;
• le rangement du matériel;
• l'absence de gallons vides ou de déchets accumulés;
• l'entretien rapide de l'intérieur du véhicule;
• le respect des endroits prévus pour le matériel.

Une unité propre et bien rangée aide toute l'équipe à travailler plus efficacement et à préserver le matériel fourni par l'entreprise.`,
    scope: "all",
  },
  inspection: {
    title: "Inspection de chantier",
    objectif: "Réussir au moins 80% des inspections de chantier.",
    texte: `Ce critère est basé sur les inspections effectuées pendant les travaux.

Lors d'une inspection, plusieurs éléments peuvent être vérifiés, par exemple :
• la propreté du chantier;
• la sécurité;
• la communication;
• le respect du contrat;
• l'organisation générale;
• la qualité du suivi avec le client.

Si l'inspection est réussie, elle améliore ou maintient le score. Si elle n'est pas réussie, elle peut faire diminuer le résultat du chef d'équipe et de l'équipe concernée.

L'objectif est de s'assurer que les chantiers sont bien suivis, sécuritaires et conformes aux attentes de l'entreprise.`,
    scope: "all",
  },
  avertissements: {
    title: "Avertissements",
    objectif: "Respecter les attentes de base de l'entreprise pendant la saison.",
    texte: `Cette section regroupe les avertissements officiels qui peuvent influencer l'admissibilité au bonus.

Chaque type d'avertissement a une limite permise pour la saison. Tant que la limite n'est pas dépassée, le critère reste conforme.

Par exemple, Retard 0/3 signifie que tu as actuellement 0 retard comptabilisé sur une limite de 3 retards permis pour la saison.

L'objectif n'est pas de pénaliser une petite erreur isolée, mais de s'assurer que le bonus récompense les employés fiables, professionnels, sécuritaires et respectueux des standards de l'entreprise.

Seuls les avertissements validés officiellement par un gestionnaire comptent dans ce calcul.`,
    renderRich: "avertissements",
    scope: "all",
  },
  // ── Critères CP ───────────────────────────────────────────────────
  cp_objectif_temps: {
    title: "Objectif temps des équipes",
    objectif: "Avoir au moins 80% des bons de travail terminés dans les heures budgétées.",
    texte: `Ce critère mesure la performance globale des équipes sur les bons de travail liés à tes dossiers pendant la saison sélectionnée.

Pour chaque bon de travail admissible, la plateforme compare les heures budgétées avec les heures réellement poinçonnées.

Si les heures réelles sont inférieures ou égales aux heures budgétées, avec la tolérance prévue, le bon est considéré conforme.

Si les heures réelles dépassent le budget, le bon est considéré à surveiller.

Ce critère permet de suivre si les équipes respectent globalement les heures prévues et d'identifier les dossiers qui demandent un meilleur suivi.`,
    scope: "cp",
  },
  cp_photos: {
    title: "Photos avant/après",
    objectif: "S'assurer que les photos requises sont ajoutées aux dossiers concernés.",
    texte: `Ce critère s'applique seulement aux dossiers où les photos avant/après sont demandées.

Lorsqu'un dossier indique que les photos sont requises, la plateforme vérifie qu'au moins une photo a été ajoutée dans la section Photos du dossier.

Un dossier est conforme si les photos sont requises et qu'au moins une photo est présente.

Un dossier est à compléter si les photos sont requises, mais qu'aucune photo n'a été ajoutée.

Si aucun dossier de la saison ne requiert de photos, le critère ne devrait pas être affiché comme un échec. Il devrait plutôt indiquer qu'aucune donnée n'est disponible pour ce critère.`,
    scope: "cp",
  },
  cp_inspection: {
    title: "Inspections de chantier",
    objectif: "S'assurer qu'une portion suffisante des chantiers est inspectée pendant la saison.",
    texte: `Ce critère vérifie le pourcentage de bons de travail qui ont au moins une inspection enregistrée.

L'objectif est qu'au moins 35% des bons de travail admissibles aient une inspection complétée.

Les inspections permettent de valider le suivi du chantier, la qualité, la sécurité, la propreté et la conformité avec les attentes de l'entreprise.

Seules les inspections officiellement enregistrées dans la plateforme sont comptabilisées.`,
    scope: "cp",
  },
  cp_completude_bt: {
    title: "Bons de travail complets",
    objectif: "S'assurer que les bons de travail sont suffisamment complets pour permettre aux équipes de bien exécuter les travaux.",
    texte: `Ce critère vérifie si les informations essentielles sont présentes dans chaque bon de travail de la saison.

Pour un BT intérieur, les éléments requis peuvent inclure :
• la description des travaux;
• les produits à utiliser;
• au moins un pointage lié au bon de travail.

Pour un BT extérieur, les éléments requis peuvent inclure :
• la description des travaux;
• les produits à utiliser;
• au moins un pointage lié au bon de travail;
• au moins une étape;
• au moins une zone.

Un bon de travail incomplet peut nuire à la planification, au suivi de production et à la clarté pour les équipes sur le terrain.`,
    scope: "cp",
  },
  cp_lavage_72h: {
    title: "Lavage à pression 72 h avant les travaux",
    objectif: "S'assurer que les lavages nécessaires sont effectués au moins 72 h avant le début des travaux.",
    texte: `Ce critère vérifie les routes de lavage associées aux dossiers suivis pendant la saison.

Pour qu'un lavage soit conforme, il doit être terminé au moins 72 h avant la première date prévue du chantier.

Exemple : si les travaux commencent le 15 juin, le lavage doit être terminé au plus tard le 12 juin.

Seules les routes de lavage terminées sont évaluées. Les lavages non terminés ne devraient pas être comptés comme conformes.

Ce critère aide à assurer une bonne préparation des surfaces avant l'arrivée de l'équipe de peinture.`,
    scope: "cp",
  },
  cp_email: {
    title: "Courriel de présentation",
    objectif: "S'assurer que les clients reçoivent leur courriel de présentation avant les travaux.",
    texte: `Ce courriel permet au client de transmettre certaines informations importantes avant le début du chantier, comme les codes de couleur.

Ce critère vérifie que les bons de travail admissibles de la saison ont bien reçu le courriel de présentation.

Un dossier est conforme si le courriel a été envoyé.

Un dossier est à compléter si le courriel aurait dû être envoyé, mais ne l'a pas été.

Ce suivi aide à mieux préparer les chantiers, éviter les oublis et réduire les échanges de dernière minute avec les clients.`,
    scope: "cp",
  },
  cp_avertissements: {
    title: "Avertissements — Chargée de projet",
    objectif: "Respecter les attentes liées au rôle de chargée de projet pendant la saison.",
    texte: `Cette section regroupe les avertissements officiels qui peuvent influencer l'admissibilité au bonus.

Les catégories sont adaptées au rôle de chargée de projet, par exemple :
• capacité à régler les problèmes de production;
• capacité à maintenir une bonne structure de travail;
• rédaction des horaires et soutien aux équipes;
• attitude;
• autres situations importantes.

Chaque catégorie a une limite permise pour la saison. Tant que la limite n'est pas dépassée, le critère reste conforme.

Seuls les avertissements officiellement validés par la direction comptent dans ce calcul.`,
    scope: "cp",
  },
  bonus_variable: {
    title: "Comprendre mon bonus potentiel",
    objectif: "Récompenser l'efficacité collective de ton équipe.",
    texte: `Le bonus potentiel est une estimation en temps réel. Il te montre ce que ton bonus pourrait représenter si la saison se terminait aujourd'hui, selon les données disponibles.

Le bonus est basé principalement sur les heures économisées par ton équipe sur les bons de travail terminés.

Chaque bon de travail a un nombre d'heures budgétées. Si ton équipe termine les travaux en utilisant moins d'heures que prévu, des heures sont économisées. Ces heures peuvent générer une valeur de bonus.

🏆 Paliers de performance

Plus ton équipe économise d'heures, plus son niveau de performance peut augmenter :
• 🥉 Bronze
• 🥈 Argent
• 🥇 Or
• 💎 Platine

Chaque niveau peut augmenter le montant gagné par heure économisée.

Ton bonus personnel dépend notamment :
• de la performance de ton équipe;
• de ton rôle dans l'équipe;
• de ta participation aux chantiers;
• des critères d'admissibilité de fin de saison.

Un bonus potentiel affiché ne veut pas dire qu'il est automatiquement versable. Le versement final dépend des critères d'admissibilité.

Un bonus à 0 $ ne veut pas nécessairement dire que tu as mal travaillé. Cela peut simplement vouloir dire que les heures économisées sont actuellement négatives ou nulles, ou que le bonus n'a pas encore généré de montant positif.

Le montant final sera confirmé à la fin de la saison, lorsque tous les critères seront vérifiés.`,
    scope: "all",
  },
  bonus_variable_cp: {
    title: "Comprendre mon bonus potentiel — Chargée de projet",
    objectif: "Récompenser la performance globale des équipes et la qualité du suivi de production.",
    texte: `Le bonus potentiel est une estimation en temps réel. Il montre ce que ton bonus pourrait représenter si la saison se terminait aujourd'hui, selon les données disponibles.

Pour la chargée de projet, le bonus est basé sur la performance globale des dossiers qu'elle accompagne pendant la saison sélectionnée.

La plateforme compare les heures budgétées et les heures réelles des bons de travail liés à ses dossiers.

Si les équipes terminent les travaux avec moins d'heures que prévu, cela crée des heures économisées. Si les équipes dépassent les heures prévues, l'écart devient négatif.

🏆 Paliers de performance

Plus la performance globale des équipes s'améliore, plus le niveau de performance peut augmenter :
• 🥉 Bronze
• 🥈 Argent
• 🥇 Or
• 💎 Platine

Chaque niveau peut augmenter le montant généré par les heures économisées.

Comme chargée de projet, ton rôle est d'aider les équipes à performer grâce à :
• une bonne planification;
• un bon suivi des bons de travail;
• une bonne communication avec les clients et les équipes;
• une bonne préparation des dossiers;
• un suivi rapide des problèmes de production.

Ton bonus potentiel est calculé selon la logique prévue pour le rôle de chargée de projet. Il tient compte de la performance globale des équipes suivies et des critères d'admissibilité de la saison.

Un bonus potentiel affiché ne veut pas dire qu'il est automatiquement versable. Le versement final dépend des critères d'admissibilité.

Si le bonus affiche 0 $, cela peut vouloir dire que les heures réelles dépassent actuellement les heures budgétées ou que la performance globale n'a pas encore généré de bonus positif.

Le montant final sera confirmé à la fin de la saison.`,
    scope: "cp",
  },
  temps_reel: {
    title: "Comprendre mes heures économisées",
    objectif: "Suivre en direct l'écart entre les heures budgétées et réelles de votre équipe — ramené à votre part personnelle.",
    texte: `Cette section te montre si ton équipe est en avance ou en retard par rapport aux heures prévues sur les bons de travail.

Chaque bon de travail a un nombre d'heures budgétées. Ce sont les heures prévues pour réaliser le chantier.

Ensuite, la plateforme compare ces heures avec les heures réellement poinçonnées sur le chantier.

Si un chantier était prévu à 100 heures et que l'équipe le termine en 90 heures, l'équipe a économisé 10 heures.

Si un chantier était prévu à 100 heures et que l'équipe prend 110 heures, l'équipe est en dépassement de 10 heures.

Les heures économisées sont calculées pour l'équipe, puis ramenées à ta participation.

Par exemple, si ton équipe économise 20 heures et que tu as travaillé environ 50% des heures sur ce chantier, ta part personnelle sera d'environ 10 heures économisées.

Si ton chiffre est positif, cela veut dire que tu contribues à une bonne performance d'équipe.

Si ton chiffre est négatif, cela veut dire que les chantiers auxquels tu as participé ont pris plus de temps que prévu. Ce n'est pas nécessairement final, car le résultat peut s'améliorer avec les prochains chantiers de la saison.

Les heures économisées servent à estimer le bonus potentiel. Le versement final du bonus dépend aussi des autres critères, comme le nombre total d'heures travaillées, les avertissements et les critères de performance.`,
    scope: "all",
  },
  temps_reel_cp: {
    title: "Comprendre la performance globale des équipes",
    objectif: "Suivre l'écart entre les heures budgétées et réelles des bons de travail liés à tes dossiers.",
    texte: `Cette section montre l'écart entre les heures budgétées et les heures réelles des bons de travail liés à tes dossiers pour la saison sélectionnée.

Les heures budgétées représentent les heures prévues sur les bons de travail.

Les heures réelles représentent les heures poinçonnées par les équipes sur ces mêmes bons de travail.

Si les équipes terminent les travaux avec moins d'heures que prévu, l'écart est positif.

Si les équipes dépassent les heures prévues, l'écart est négatif.

Pour la chargée de projet, ce résultat n'est pas basé sur des heures personnelles de chantier. Il reflète plutôt la performance globale des équipes que tu accompagnes dans la planification, le suivi des dossiers, la coordination et la production.

Le bonus potentiel de la chargée de projet est ensuite calculé selon la logique prévue pour ce rôle, à partir de la performance globale des équipes et des critères d'admissibilité de la saison.`,
    scope: "cp",
  },
  heures_detail_bt: {
    title: "Détail de mes heures par bon de travail",
    objectif: "Voir les bons de travail qui ont servi à calculer tes heures budgétées, réelles et économisées.",
    texte: `Cette section montre les bons de travail qui ont servi à calculer tes heures budgétées, tes heures réelles et tes heures économisées.

Pour chaque chantier, la plateforme compare les heures prévues avec les heures réellement travaillées par l'équipe.

Si le chantier prend moins d'heures que prévu, l'écart est positif.

Si le chantier prend plus d'heures que prévu, l'écart est négatif.

Ta part personnelle est calculée selon ta présence sur ce chantier. Plus tu as travaillé sur un chantier, plus ta part de l'écart est grande.

📊 Colonnes affichées :

• Budget BT — Heures prévues pour le chantier.
• Réel BT — Heures réellement poinçonnées par l'équipe sur ce chantier.
• Écart BT — Différence entre les heures prévues et les heures réelles.
• Ma part % — Ta participation dans les heures du chantier.
• Mes heures — Le nombre d'heures que tu as personnellement travaillées sur ce chantier.
• Mes heures économisées estimées — Ta portion personnelle de l'écart du chantier.`,
    scope: "all",
  },
  heures_detail_bt_cp: {
    title: "Détail des heures des équipes par BT — Vue chargée de projet",
    objectif: "Voir les bons de travail qui ont servi à calculer la performance globale des équipes.",
    texte: `Cette section montre les bons de travail liés à tes dossiers et la performance des équipes pendant la saison sélectionnée.

Pour chaque chantier, la plateforme compare les heures prévues avec les heures réellement poinçonnées par les équipes.

Si le chantier prend moins d'heures que prévu, l'écart global est positif.

Si le chantier prend plus d'heures que prévu, l'écart global est négatif.

Ces écarts servent à estimer le bonus potentiel de la chargée de projet, selon la logique prévue pour ce rôle.

📊 Colonnes affichées :

• Budget BT — Heures prévues pour le chantier.
• Réel BT — Heures réellement poinçonnées par l'équipe sur ce chantier.
• Écart BT — Différence entre les heures prévues et les heures réelles.

Pour la chargée de projet, ce résultat n'est pas basé sur des heures personnelles de chantier. Il reflète la performance globale des équipes que tu accompagnes.`,
    scope: "cp",
  },
};

// Sous-critères avec scope (identiques au backend)
const SOUS_CRITERES_CHEF = ["gallons_avant_14h", "qualite_travail", "gestion_inventaire", "materiel_bon_travail", "ajouts_client", "ajuster_personnel", "pauses_respectees"];
const SOUS_CRITERES_ALL = ["contrat_respecte", "client_satisfait", "inventaire_outils"];

const formatCAD = (n) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n || 0);

// 🆕 2026-02-21 — Phase 1 refonte UX : statut positif au lieu de "Non éligible"
// Détermine si la saison sélectionnée est encore en cours (versement non final).
// Format attendu : "Été 2026" | "Hiver 2026-2027" | "Année 2026" | "Tous les BT" | autre
const isSaisonEnCours = (saison) => {
  if (!saison) return true;
  const s = String(saison);
  if (/tous les bt/i.test(s)) return false; // rétro = définitif
  const now = new Date();
  const m = now.getMonth() + 1; // 1-12
  const y = now.getFullYear();
  // Été {YYYY} → 1er mai au 31 oct YYYY
  let mEte = s.match(/(?:été|ete)\s+(\d{4})/i);
  if (mEte) {
    const yy = parseInt(mEte[1], 10);
    return y < yy || (y === yy && m >= 5 && m <= 10);
  }
  // Hiver {YYYY}-{YYYY+1} → 1er nov YYYY au 30 avril YYYY+1
  let mHiv = s.match(/hiver\s+(\d{4})-(\d{4})/i);
  if (mHiv) {
    const y1 = parseInt(mHiv[1], 10);
    const y2 = parseInt(mHiv[2], 10);
    if (y < y1) return true;
    if (y === y1 && m >= 11) return true;
    if (y === y2 && m <= 4) return true;
    return false;
  }
  // Année / YTD {YYYY}
  let mAnn = s.match(/(?:année|annee|ytd)\s+(\d{4})/i);
  if (mAnn) return y <= parseInt(mAnn[1], 10);
  return true; // par défaut on suppose en cours
};

// Statut d'admissibilité positif (Phase 1)
// Retourne {label, sub, color, bg, border, icon}
const getEligibilityStatus = ({ eligible, bonusEstime, saisonEnCours, heuresEcoNeg }) => {
  if (eligible) {
    return {
      label: '✅ Critères atteints',
      sub: saisonEnCours ? 'Versement à confirmer en fin de saison' : 'Bonus versable',
      color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300',
    };
  }
  if (saisonEnCours) {
    // En cours → message positif/neutre
    if (heuresEcoNeg) {
      return {
        label: '🌱 En progression',
        sub: 'Continue, la saison n\'est pas terminée — les résultats peuvent évoluer',
        color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-300',
      };
    }
    return {
      label: '⏳ Versement à confirmer',
      sub: 'Certains critères ne sont pas encore atteints — c\'est normal en cours de saison',
      color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300',
    };
  }
  // Saison terminée + non éligible → définitif
  return {
    label: '🚫 Non admissible cette saison',
    sub: 'Les critères n\'ont pas été atteints sur la saison — rendez-vous la prochaine !',
    color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-300',
  };
};


// 🆕 2026-02-21 Phase 4 — Projection linéaire des heures de saison.
// Retourne les bornes [debut, fin] de la saison sous forme de Date (UTC),
// ou [null, null] si la saison est inconnue / "Tous les BT".
const getSaisonBounds = (saison) => {
  if (!saison) return [null, null];
  const s = String(saison);
  if (/tous les bt/i.test(s)) return [null, null];
  let m = s.match(/(?:été|ete)\s+(\d{4})/i);
  if (m) {
    const y = parseInt(m[1], 10);
    return [new Date(Date.UTC(y, 4, 1)), new Date(Date.UTC(y, 9, 31))]; // 1er mai → 31 oct
  }
  m = s.match(/hiver\s+(\d{4})-(\d{4})/i);
  if (m) {
    const y1 = parseInt(m[1], 10);
    const y2 = parseInt(m[2], 10);
    return [new Date(Date.UTC(y1, 10, 1)), new Date(Date.UTC(y2, 3, 30))]; // 1er nov → 30 avril
  }
  m = s.match(/(?:année|annee|ytd)\s+(\d{4})/i);
  if (m) {
    const y = parseInt(m[1], 10);
    return [new Date(Date.UTC(y, 0, 1)), new Date(Date.UTC(y, 11, 31))];
  }
  return [null, null];
};

// Retourne { projection, jours_ecoules, jours_total, message, ton } ou null si
// la projection n'est pas calculable (saison non commencée / terminée / Tous les BT).
const computeProjectionHeures = (saison, heuresTotal, seuil) => {
  const [debut, fin] = getSaisonBounds(saison);
  if (!debut || !fin || heuresTotal == null || !seuil) return null;
  const now = new Date();
  // Saison pas encore commencée
  if (now < debut) return null;
  // Saison terminée
  if (now > fin) return null;
  const MS_PER_DAY = 86400000;
  const joursEcoules = Math.floor((now - debut) / MS_PER_DAY) + 1;
  const joursTotal = Math.floor((fin - debut) / MS_PER_DAY) + 1;
  if (joursEcoules < 7) return null; // pas assez d'historique pour projeter (< 1 semaine)
  const rythme = heuresTotal / joursEcoules;
  const projection = Math.round(rythme * joursTotal);
  const enBonneVoie = projection >= seuil;
  const message = enBonneVoie
    ? `À ce rythme, tu es projeté à environ ${projection} h cette saison. Tu es en bonne voie pour atteindre ce critère.`
    : `À ce rythme, tu pourrais être sous l'objectif de ${seuil} h (~${projection} h projetées). Augmenter ta présence dans les prochaines semaines peut t'aider à atteindre ce critère.`;
  return {
    projection,
    jours_ecoules: joursEcoules,
    jours_total: joursTotal,
    message,
    ton: enBonneVoie ? 'bonne_voie' : 'a_ameliorer',
  };
};


// ─── Composant Critère Card ────────────────────────────────────────────────────

const CritereCard = ({ label, score, seuil, ok, totalEvals, icon: Icon, onExplain, onDetails, isChef, extra, saisonEnCours = true }) => {
  const hasData = score !== null && score !== undefined;
  const pct = hasData ? score : null;
  // 🆕 2026-02-21 Phase 1 — rouge UNIQUEMENT si saison terminée. Sinon orange "à améliorer".
  const statusColor = !hasData
    ? "text-slate-400"
    : ok
      ? "text-emerald-600"
      : (saisonEnCours ? 'text-amber-600' : 'text-red-500');
  const borderColor = !hasData
    ? "border-slate-100"
    : ok
      ? "border-l-emerald-400"
      : (saisonEnCours ? 'border-l-amber-400' : 'border-l-red-400');
  const bgColor = !hasData
    ? 'bg-slate-100'
    : ok
      ? 'bg-emerald-100'
      : (saisonEnCours ? 'bg-amber-100' : 'bg-red-100');

  return (
    <Card className={`border-l-4 ${borderColor} hover:shadow-md transition-shadow`}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${bgColor}`}>
              <Icon className={`w-5 h-5 ${statusColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800 text-sm">{label}</span>
                {extra}
              </div>
              {hasData ? (
                <div className="mt-1.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-lg font-bold ${statusColor}`}>{pct?.toFixed(0)}%</span>
                    <span className="text-xs text-slate-400">/ {seuil}% requis</span>
                    {totalEvals !== undefined && <span className="text-xs text-slate-400">({totalEvals} évaluations)</span>}
                  </div>
                  <Progress value={Math.min(pct, 100)} className="h-1.5" />
                </div>
              ) : (
                <p className="text-xs text-slate-400 mt-1">Aucune donnée pour cette saison</p>
              )}
            </div>
          </div>

          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="text-[11px] px-2 py-1 h-auto gap-1 text-blue-600 hover:bg-blue-50" onClick={onExplain} title="Comprendre ce critère">
              <HelpCircle className="w-3.5 h-3.5" /> Comprendre
            </Button>
            {onDetails && hasData && (
              <Button size="sm" variant="ghost" className="text-[11px] px-2 py-1 h-auto gap-1 text-slate-600 hover:bg-slate-100" onClick={onDetails} title="Voir le détail">
                Détails <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// ─── Bloc enrichi pour le critère « Avertissements » (texte officiel Phase 50)
//     Composant extrait dans /components/bonus/AvertissementsRichBlock.jsx
//     pour réutilisation sur /gestion-avertissements (source unique de vérité).


// ─── Composant Dialogue Explication ───────────────────────────────────────────

const ExplicationDialog = ({ open, onClose, critere }) => {
  if (!critere) return null;
  const exp = EXPLICATIONS[critere] || {};
  const isBonus = critere === 'bonus_variable';
  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* 🆕 Phase E3 (2026-05-04) — Tooltip "?" tronqué sur mobile.
          Causes : pas de max-h + overflow-y, et pas de break-words sur le texte
          long → débordait hors écran, fin de phrases coupée ("tinue !" / "00h)").
          Fix : adaptatif mobile (w-[calc(100vw-1rem)]), scroll vertical limité,
          break-words sur le contenu, max-h sécurisé.  */}
      <DialogContent
        className={`w-[calc(100vw-1rem)] sm:w-full max-h-[85vh] overflow-y-auto ${
          isBonus ? "max-w-2xl" : "max-w-lg"
        }`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 break-words">
            <Info className="w-5 h-5 text-blue-500 shrink-0" />
            <span className="break-words">{exp.title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Objectif</div>
            <p className="text-sm font-medium text-emerald-800 break-words">{exp.objectif}</p>
          </div>
          {exp.renderRich === "avertissements" ? (
            <AvertissementsRichBlock />
          ) : (
            <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed break-words">
              {exp.texte}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ─── Composant Dialogue Détails ────────────────────────────────────────────────

const DetailsDialog = ({ open, onClose, critere, data, isChef }) => {
  if (!critere || !data) return null;

  const details = data.details || [];
  const exp = EXPLICATIONS[critere] || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{exp.title} — Détails</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Résumé */}
          <div className="flex gap-4 p-3 bg-slate-50 rounded-lg text-sm">
            <div><span className="text-slate-500">Score :</span> <strong className={data.ok ? "text-emerald-600" : "text-red-500"}>{data.score?.toFixed(0) ?? "—"}%</strong></div>
            <div><span className="text-slate-500">Réussis :</span> <strong>{data.reussis}/{data.total}</strong></div>
            <div><span className="text-slate-500">Seuil :</span> <strong>{data.seuil}%</strong></div>
          </div>

          {/* Critère Avertissements : affichage spécial */}
          {critere === "avertissements" && (
            <div className="space-y-2">
              {Object.entries(data.par_type || {}).map(([type, info]) => (
                <div key={type} className={`border rounded-lg p-3 ${!info.ok ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-slate-800">{info.label}</span>
                    <span className={`text-sm font-bold ${!info.ok ? 'text-red-600' : 'text-slate-700'}`}>{info.count}/{info.seuil}</span>
                  </div>
                  {info.details?.map((av) => {
                    const titre = av.categorie || av.type || 'Avertissement';
                    const date = av.date_incident || av.created_at?.slice(0, 10);
                    const auteur = av.auteur_nom || av.responsable_nom || av.created_by_nom;
                    const contexte = av.client_nom || av.numero_dossier;
                    return (
                      <div key={av.id} className="mt-2 ml-2 border-l-2 border-slate-300 pl-3 py-1 text-xs">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="font-semibold text-slate-700">{titre}</span>
                          <span className="text-slate-400 text-[10px]">{date || '—'}</span>
                        </div>
                        {av.description && <div className="text-slate-600">{av.description}</div>}
                        {av.commentaire && <div className="text-slate-500 italic mt-0.5">« {av.commentaire} »</div>}
                        {(auteur || contexte || av.severite) && (
                          <div className="text-slate-400 text-[10px] mt-1 flex gap-2 flex-wrap">
                            {auteur && <span>par {auteur}</span>}
                            {contexte && <span>· {contexte}</span>}
                            {av.severite && <span>· sévérité : {av.severite}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Critère CP — Objectif temps des équipes */}
          {critere === "objectif_temps_cp" && (
            <div className="space-y-2">
              <div className="flex gap-4 p-3 bg-purple-50 rounded-lg text-sm">
                <div><span className="text-slate-500">Dans le budget :</span> <strong>{data.dans_budget}/{data.total}</strong></div>
                <div><span className="text-slate-500">Score :</span> <strong className={data.ok ? "text-emerald-600" : "text-red-500"}>{data.score?.toFixed(0)}%</strong></div>
                <div><span className="text-slate-500">Seuil :</span> <strong>80%</strong></div>
              </div>
              {(data.details || []).map((d, i) => (
                <div key={d.id || d._id || i} className={`border rounded-lg p-3 ${!d.ok ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {d.ok
                        ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                      <span className="text-sm font-medium text-slate-800">{d.client_nom || `Bon ${i + 1}`}</span>
                    </div>
                    <Badge variant="outline" className={`text-xs ${!d.ok ? 'text-red-600 border-red-300' : 'text-emerald-600 border-emerald-300'}`}>
                      {d.heures_reelles}h / {d.heures_budget}h
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Critère CP — Bons de travail complets (détails des issues) */}
          {critere === "completude_bt" && (
            <div className="space-y-2">
              <div className="flex gap-4 p-3 bg-purple-50 rounded-lg text-sm">
                <div><span className="text-slate-500">Complets :</span> <strong>{data.complets}/{data.total}</strong></div>
                <div><span className="text-slate-500">Score :</span> <strong className={data.ok ? "text-emerald-600" : "text-red-500"}>{data.score?.toFixed(0)}%</strong></div>
              </div>
              {(data.details || []).map((d, i) => (
                <div key={d.id || d._id || i} className={`border rounded-lg p-3 ${!d.complet ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {d.complet
                        ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                      <span className="text-sm font-medium text-slate-800">{d.client_nom || `Bon ${i + 1}`}</span>
                    </div>
                  </div>
                  {!d.complet && d.issues?.length > 0 && (
                    <div className="mt-2 ml-6 space-y-0.5">
                      {d.issues.map((issue, j) => (
                        <div key={j} className="text-xs text-red-600 flex items-center gap-1">
                          <XCircle className="w-3 h-3 shrink-0" /> {issue}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Autres critères : liste des évaluations */}
          {critere !== "avertissements" && details.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">Aucune évaluation cette saison</p>
          )}

          {critere !== "avertissements" && details.map((d, i) => (
            <div key={d.id || d._id || i} className={`border rounded-lg p-3 ${!d.passed ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {d.passed
                    ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    : <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                  }
                  <span className="text-sm font-medium text-slate-800">
                    {/* 🆕 2026-02-21 Phase 8b — Prioriser client_nom (lisible) vs UUID */}
                    {d.client_nom || d.titre || d.date || d.inspection_id || `Évaluation ${i + 1}`}
                  </span>
                </div>
                {d.score_pct !== undefined && (
                  <Badge variant="outline" className={`text-xs ${!d.passed ? 'text-red-600 border-red-300' : 'text-emerald-600 border-emerald-300'}`}>
                    {d.score_pct?.toFixed(0)}%
                  </Badge>
                )}
                {d.ratio_pct !== undefined && (
                  <Badge variant="outline" className={`text-xs ${!d.passed ? 'text-red-600 border-red-300' : 'text-emerald-600 border-emerald-300'}`}>
                    {d.ratio_pct?.toFixed(0)}% du budget
                  </Badge>
                )}
              </div>

              {/* Détails spécifiques */}
              {d.heures_budgetees !== undefined && (
                <div className="mt-1 text-xs text-slate-500 ml-6">
                  Budget: {d.heures_budgetees?.toFixed(2).replace('.', ',')}h | Réelles: {d.heures_reelles?.toFixed(2).replace('.', ',')}h
                </div>
              )}
              {d.has_service_call && d.service_calls?.length > 0 && (
                <div className="mt-1 text-xs text-red-500 ml-6">
                  {d.service_calls.length} service call(s) enregistré(s)
                </div>
              )}
              {d.commandes_tardives > 0 && (
                <div className="mt-1 text-xs text-amber-600 ml-6">
                  ⚠️ {d.commandes_tardives} commande(s) tardive(s) après 15h30
                </div>
              )}
              {d.formulaire_rempli === false && (
                <div className="mt-1 text-xs text-slate-400 ml-6">Formulaire non rempli → réussi par défaut</div>
              )}
              {d.nb_total !== undefined && d.nb_total > 0 && (
                <div className="mt-1 text-xs text-slate-500 ml-6">{d.nb_reussi}/{d.nb_total} critères réussis</div>
              )}

              {/* Sous-critères Gestion du Contrat */}
              {critere === "gestion_contrat" && d.formulaire_rempli && d.sous_criteres && (
                <div className="mt-2 ml-6 space-y-1">
                  {Object.entries(d.sous_criteres)
                    .filter(([id]) => isChef || SOUS_CRITERES_ALL.includes(id))
                    .map(([id, sc]) => (
                      <div key={id} className="text-xs">
                        <div className="flex items-center gap-1.5">
                          {sc.passed
                            ? <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                            : <XCircle className="w-3 h-3 text-red-400 shrink-0" />
                          }
                          <span className={sc.passed ? "text-slate-600" : "text-red-600"}>{sc.label}</span>
                          {sc.scope === "chef" && !isChef && <Badge variant="outline" className="text-[9px] ml-1">Chef</Badge>}
                          {sc.auto_rempli && <Badge className="text-[9px] ml-1 bg-orange-100 text-orange-700">Auto</Badge>}
                        </div>
                        {sc.note && <p className="text-slate-400 ml-5 italic">{sc.note}</p>}
                      </div>
                    ))
                  }
                </div>
              )}

              {d.notes && <div className="mt-1 text-xs text-slate-400 ml-6 italic">"{d.notes}"</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ─── Page Principale ────────────────────────────────────────────────────────────

export const MonBonus = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canDo } = usePermissions() || {};
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC.
  //   - `mon_bonus_admin_voir_autres` : dropdown "Employé" pour consulter le bonus d'un autre.
  //   - Édition du montant fixe : reste Cat H (identité Owner, opération financière — hors scope RBAC matrice).
  const canVoirBonusAutres = !!(canDo && canDo('mon_bonus_admin_voir_autres'));
  // 🆕 [v13.66 Volet B — 2026-02-28] Vue tableau de bord manager (RBAC centralisé).
  //   Aucun `role === 'proprietaire'` hardcodé : contrôle 100% via clé matrice.
  const canVoirDashboardGlobal = !!(canDo && canDo('bonus_dashboard_global'));
  const isProprietaire     = estProprietaire(user); // 🔒 Cat H — édition montant fixe
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(canVoirBonusAutres ? "" : (user?.employee_id || ""));
  const [saisons, setSaisons] = useState([]);
  const [saison, setSaison] = useState("");
  const [perfData, setPerfData] = useState(null);
  const [bonusData, setBonusData] = useState(null);
  const [bonusFixeData, setBonusFixeData] = useState(null);
  const [bonusFixeConfig, setBonusFixeConfig] = useState({ montant_par_saison: 0 });
  const [editingMontant, setEditingMontant] = useState(false);
  const [newMontant, setNewMontant] = useState(0);
  const [loading, setLoading] = useState(false);

  // 🆕 [v13.66 Volet B] État Dashboard global (manager)
  //   `vue = 'perso' | 'global'` — défaut 'global' si autorisé (élément demandé par la propriétaire).
  const [vue, setVue] = useState(canVoirDashboardGlobal ? 'global' : 'perso');
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [criteresSectionOpen, setCriteresSectionOpen] = useState(false);

  // Dialogs
  const [explDialog, setExplDialog] = useState(null); // key du critère
  const [detailDialog, setDetailDialog] = useState(null); // {key, data}
  // 🚀 P1 (2026-05-04) — Modal détails BT (transparence "Réelles sur mes BT")
  const [showDetailsBt, setShowDetailsBt] = useState(false);
  // 🆕 2026-02-21 Phase 8 — Modale détails CP (clic sur critère CP)
  const [cpDetailDialog, setCpDetailDialog] = useState(null); // { critereKey, critereData }
  const [detailsBt, setDetailsBt] = useState(null); // {saison, details: [...], total_perso}
  const [loadingDetailsBt, setLoadingDetailsBt] = useState(false);

  useEffect(() => {
    // 🛡️ Phase E3 finition (2026-05-04) — Bug bloquant identifié :
    //   /api/bonus/fixe/config est restreint proprio/CP (403 sinon, depuis A1).
    //   Avec Promise.all, un seul rejet casse TOUT le bootstrap, donc les
    //   peintres/chefs n'avaient jamais leur Select Saison rempli → aucune
    //   donnée ne s'affichait. Solution : Promise.allSettled + fallbacks.
    Promise.allSettled([
      axios.get(`${api.baseUrl}/employees`).then(r => r.data),
      axios.get(`${api.baseUrl}/bonus/saisons`).then(r => r.data),
      axios.get(`${api.baseUrl}/bonus/fixe/config`).then(r => r.data),
    ]).then(([empRes, saisonRes, configRes]) => {
      const empData = empRes.status === 'fulfilled' ? empRes.value : {};
      const saisonData = saisonRes.status === 'fulfilled' ? saisonRes.value : {};
      const configData = configRes.status === 'fulfilled' ? configRes.value : { montant_par_saison: 0 };

      // 🔄 Phase A2.2 (2026-05-04) — Item #4b : exclure sous-traitants/installateurs
      // de la liste affichée (ils ne reçoivent aucun bonus, ne doivent pas apparaître).
      const ROLES_EXCLUS = ["sous_traitant", "installateur"];
      const allEmps = empData.employees || empData || [];
      const filtered = (Array.isArray(allEmps) ? allEmps : []).filter(e => {
        const rt = (e.role_terrain || "").toLowerCase();
        const te = (e.type_employe || "").toLowerCase();
        return !ROLES_EXCLUS.includes(rt) && !ROLES_EXCLUS.includes(te);
      });
      setEmployees(filtered);
      setSaisons(saisonData.saisons || []);
      setSaison(saisonData.courante || "");
      setBonusFixeConfig(configData);
      setNewMontant(configData.montant_par_saison || 0);
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedEmp || !saison) return;
    setLoading(true);
    try {
      const [perfRes, bonusRes] = await Promise.all([
        axios.get(`${api.baseUrl}/performance/employe/${selectedEmp}?saison=${encodeURIComponent(saison)}`),
        axios.get(`${api.baseUrl}/bonus/employe/${selectedEmp}?saison=${encodeURIComponent(saison)}`),
      ]);
      const perf = perfRes.data;
      const bonus = bonusRes.data;
      setPerfData(perf);
      setBonusData(bonus);

      // Si c'est un chef, charger aussi le bonus fixe
      if (perf.is_chef) {
        const fixeRes = await axios.get(`${api.baseUrl}/bonus/fixe/employe/${selectedEmp}?saison=${encodeURIComponent(saison)}`);
        const fixe = fixeRes.data;
        setBonusFixeData(fixe);
      } else {
        setBonusFixeData(null);
      }
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [selectedEmp, saison]);

  useEffect(() => { loadData(); }, [loadData]);

  // 🆕 [v13.66 Volet B — 2026-02-28] Fetch dashboard global (manager)
  //   Chargé UNIQUEMENT quand : `vue === 'global'` ET `canDo('bonus_dashboard_global')` ET `saison` prêt.
  const loadDashboard = useCallback(async () => {
    if (!canVoirDashboardGlobal || vue !== 'global' || !saison) return;
    setLoadingDashboard(true);
    try {
      const r = await axios.get(
        `${api.baseUrl}/bonus/dashboard-global?saison=${encodeURIComponent(saison)}`,
      );
      setDashboardData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error("Accès refusé — vue globale non autorisée pour votre rôle");
      } else {
        toast.error("Erreur chargement tableau de bord global");
      }
      setDashboardData(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [canVoirDashboardGlobal, vue, saison]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // 🚀 P1 (2026-05-04) — Fetch détails BT à l'ouverture du modal "Réelles sur mes BT"
  useEffect(() => {
    if (!showDetailsBt || !selectedEmp || !saison) return;
    setLoadingDetailsBt(true);
    axios
      .get(`${api.baseUrl}/bonus/employe/${selectedEmp}/details-bt?saison=${encodeURIComponent(saison)}`)
      .then((r) => setDetailsBt(r.data))
      .catch((e) => console.error("Erreur fetch details-bt:", e))
      .finally(() => setLoadingDetailsBt(false));
  }, [showDetailsBt, selectedEmp, saison]);

  const saveMontantFixe = async () => {
    try {
      await axios.put(`${api.baseUrl}/bonus/fixe/employe/${selectedEmp}`, {
        montant_fixe: parseFloat(newMontant)
      });
      setEditingMontant(false);
      toast.success("Bonus fixe mis à jour dans la fiche de l'employé");
      loadData();
    } catch (e) {
      toast.error("Erreur sauvegarde");
    }
  };

  const palierInfo = bonusData?.equipe?.palier ? PALIER_CONFIG[bonusData.equipe.palier.niveau] || PALIER_CONFIG.Aucun : null;
  const isChef = perfData?.is_chef || false;
  const isCP = perfData?.is_cp || false;
  // 🆕 2026-02-21 Phase 1 — Helper saison en cours pour adapter les couleurs/textes (orange si en cours, rouge si fini)
  // 🐛 Fix 2026-02-21 — utilisait `selectedSaison` (n'existe pas) → toujours true par défaut. Correction : `saison`.
  const saisonEnCours = isSaisonEnCours(saison);
  const cpCriteres = perfData?.cp_criteres || {};

  const avertOk = perfData ? Object.values(perfData.avertissements?.par_type || {}).every(v => v.ok) : true;
  const totalAvert = perfData?.avertissements?.total || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Mon Bonus</h1>
          <p className="text-slate-500 mt-1 text-sm">Bonus saisonnier et critères d'admissibilité</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Sélecteurs */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-3 flex-wrap items-end">
            {/* 🆕 [v13.66 Volet B] Toggle Vue perso / globale — RBAC only */}
            {canVoirDashboardGlobal && (
              <div className="flex flex-col gap-1" data-testid="vue-toggle-wrapper">
                <label className="text-sm font-medium text-slate-700 block">Vue</label>
                <div className="inline-flex rounded-lg border border-slate-300 bg-slate-50 p-0.5">
                  <button
                    type="button"
                    onClick={() => setVue('global')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                      vue === 'global'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="btn-vue-global"
                  >
                    Tableau de bord (manager)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVue('perso')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                      vue === 'perso'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid="btn-vue-perso"
                  >
                    Vue personnelle
                  </button>
                </div>
              </div>
            )}
            {canVoirBonusAutres && vue === 'perso' && (
              <div className="flex-1 min-w-48">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Employé</label>
                <Select value={selectedEmp} onValueChange={setSelectedEmp}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner un employé..." /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}
                        {e.role && <span className="text-slate-400 ml-1">— {e.role}</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-44">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Saison</label>
              <Select value={saison} onValueChange={setSaison}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {saisons.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 🆕 [v13.66 Volet B] Vue Dashboard global — manager */}
      {canVoirDashboardGlobal && vue === 'global' && (
        <BonusDashboardGlobal
          data={dashboardData}
          loading={loadingDashboard}
          saison={saison}
        />
      )}

      {loading && vue === 'perso' && <div className="text-center py-8 text-slate-500">Calcul en cours...</div>}

      {!loading && perfData && vue === 'perso' && (
        <>
          {/* 🆕 2026-04-30 — Bannière temps réel : mes ±h personnelles */}
          <Card
            className={`border-2 ${
              (bonusData?.heures_economisees_perso || 0) > 0
                ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50'
                : (bonusData?.heures_economisees_perso || 0) < 0
                  ? 'border-red-300 bg-gradient-to-br from-red-50 to-rose-50'
                  : 'border-slate-200 bg-slate-50'
            }`}
            data-testid="card-temps-reel-perso"
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  {(bonusData?.heures_economisees_perso || 0) > 0
                    ? <TrendingUp className="w-10 h-10 text-emerald-500" />
                    : (bonusData?.heures_economisees_perso || 0) < 0
                      ? <TrendingDown className="w-10 h-10 text-red-400" />
                      : <Clock className="w-10 h-10 text-slate-400" />
                  }
                  <div>
                    <div className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      {/* 🆕 2026-02-21 Phase 7 — Libellé adapté CP (spec Message 2 §2) */}
                      {isCP ? 'Performance globale des équipes' : 'Mes heures en temps réel'}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 rounded-full"
                        onClick={() => setExplDialog(isCP ? 'temps_reel_cp' : 'temps_reel')}
                        title={isCP ? 'Comprendre la performance des équipes' : 'Comprendre mes heures'}
                      >
                        <HelpCircle className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                      </Button>
                    </div>
                    <div
                      className={`text-4xl font-bold ${
                        (bonusData?.heures_economisees_perso || 0) > 0
                          ? 'text-emerald-700'
                          : (bonusData?.heures_economisees_perso || 0) < 0
                            ? 'text-red-600'
                            : 'text-slate-600'
                      }`}
                      data-testid="heures-eco-perso-value"
                    >
                      {(bonusData?.heures_economisees_perso || 0) > 0 ? '+' : ''}
                      {(bonusData?.heures_economisees_perso || 0).toFixed(2).replace('.', ',')}h
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {/* 🆕 Phase 7 — Messages adaptés au rôle CP (vs terrain) */}
                      {(bonusData?.heures_economisees_perso || 0) > 0
                        ? (isCP
                            ? '🎉 Les équipes sont en avance — bonne performance globale !'
                            : '🎉 Tu es en avance — chaque heure économisée alimente ton bonus !')
                        : (bonusData?.heures_economisees_perso || 0) < 0
                          ? (isCP
                              ? '⚠️ Les équipes dépassent le budget — à surveiller pour la fin de saison.'
                              : '⚠️ Tu es au-dessus du budget — il faut rattraper avant fin de saison.')
                          : (isCP
                              ? 'Aucun écart pour l\'instant — la performance peut évoluer.'
                              : 'Aucun écart pour l\'instant — continue !')}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
                  {/* 🆕 2026-05-23 (refonte Eliot) — Vue cohérente sur les BT admissibles :
                      Budget total des BT, Réel total, Écart global, Ma part, Mes h. éco, Bonus.
                      Les BT à l'heure sont EXCLUS de ces totaux. */}
                  <button
                    type="button"
                    onClick={() => setShowDetailsBt(true)}
                    title={isCP
                      ? "Total des heures BUDGÉTÉES de tous les BT liés à tes dossiers (BT à l'heure exclus)."
                      : "Total des heures BUDGÉTÉES de TOUS les BT où tu as travaillé (BT à l'heure exclus). C'est le total équipe, pas ta part personnelle."}
                    className="text-left rounded hover:bg-blue-50 px-1 -mx-1 transition cursor-pointer group"
                    data-testid="btn-details-bt-budget"
                  >
                    <div className="text-lg font-bold text-slate-700 group-hover:text-blue-700" data-testid="heures-budgetees-total-bt">
                      {(bonusData?.heures_budgetees_total_bt ?? bonusData?.heures_budgetees_perso ?? 0).toFixed(2).replace('.', ',')}h
                    </div>
                    <div className="text-[11px] text-slate-500 group-hover:text-blue-600">
                      {isCP ? 'Budget total BT suivis' : 'Budget total des BT où j\'ai travaillé'} <span className="opacity-60">▾</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetailsBt(true)}
                    title={isCP
                      ? "Total des heures RÉELLES poinçonnées par les équipes sur tes dossiers (BT à l'heure exclus)."
                      : "Total des heures RÉELLES (toutes équipes) sur ces mêmes BT admissibles. BT à l'heure exclus."}
                    className="text-left rounded hover:bg-blue-50 px-1 -mx-1 transition cursor-pointer group"
                    data-testid="btn-details-bt"
                  >
                    <div className="text-lg font-bold text-slate-700 group-hover:text-blue-700" data-testid="heures-reelles-total-bt">
                      {(bonusData?.heures_reelles_total_bt ?? bonusData?.heures_reelles_perso ?? 0).toFixed(2).replace('.', ',')}h
                    </div>
                    <div className="text-[11px] text-slate-500 group-hover:text-blue-600">
                      {isCP ? 'Réel total équipes' : 'Heures réelles totales de ces BT'} <span className="opacity-60">▾</span>
                    </div>
                  </button>
                  {/* Écart global BT */}
                  <div className="text-left">
                    <div className={`text-lg font-bold ${
                      (bonusData?.ecart_global_bt ?? 0) > 0 ? 'text-emerald-600'
                      : (bonusData?.ecart_global_bt ?? 0) < 0 ? 'text-red-500' : 'text-slate-500'
                    }`} data-testid="ecart-global-bt">
                      {(bonusData?.ecart_global_bt ?? 0) > 0 ? '+' : ''}
                      {(bonusData?.ecart_global_bt ?? 0).toFixed(2).replace('.', ',')}h
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Écart global des BT
                    </div>
                  </div>
                  {/* Ma part personnelle */}
                  <div className="text-left">
                    <div className="flex items-center gap-1">
                      <div className="text-lg font-bold text-blue-700" data-testid="ma-part-pct">
                        {((bonusData?.ma_part_pct ?? 0) * 100).toFixed(1).replace('.', ',')}%
                      </div>
                      <span
                        className="text-slate-400 cursor-help"
                        title={
                          `Ta part = (tes heures sur les BT admissibles) ÷ (heures totales de l'équipe sur ces BT). ` +
                          `Plus tu fais d'heures sur des BT économiques, plus ta part augmente. ` +
                          `Exemple : tu as fait ${(bonusData?.heures_reelles_perso ?? 0).toFixed(2).replace('.', ',')}h ` +
                          `sur des BT où l'équipe a fait ${(bonusData?.heures_reelles_total_bt ?? 0).toFixed(2).replace('.', ',')}h au total ` +
                          `→ ta part = ${((bonusData?.ma_part_pct ?? 0) * 100).toFixed(1).replace('.', ',')}%.`
                        }
                        data-testid="ma-part-info"
                      >
                        <Info className="w-3 h-3" />
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Ma part estimée
                    </div>
                  </div>
                  {/* Mes heures économisées (calculées via ma_part × écart) */}
                  <div className="text-left">
                    <div className={`text-lg font-bold ${
                      (bonusData?.heures_economisees_perso ?? 0) > 0 ? 'text-emerald-600'
                      : (bonusData?.heures_economisees_perso ?? 0) < 0 ? 'text-red-500' : 'text-slate-500'
                    }`} data-testid="mes-heures-economisees">
                      {(bonusData?.heures_economisees_perso ?? 0) > 0 ? '+' : ''}
                      {(bonusData?.heures_economisees_perso ?? 0).toFixed(2).replace('.', ',')}h
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Mes heures économisées estimées
                    </div>
                  </div>
                  {/* Bonus potentiel */}
                  <div>
                    <div className="text-lg font-bold text-blue-700" data-testid="bonus-simule-value">
                      {formatCAD(bonusData?.bonus_simule ?? bonusData?.bonus_estime ?? 0)}
                    </div>
                    <div className="text-[11px] text-slate-500">Bonus potentiel estimé</div>
                    {bonusData && !bonusData.eligible && (bonusData.bonus_estime ?? 0) > 0 && (
                      <div className="text-[10px] text-amber-700 mt-0.5" data-testid="bonus-potentiel-petit">
                        ⏳ Versement à confirmer
                      </div>
                    )}
                    {bonusData?.eligible && (bonusData.heures_economisees_perso ?? 0) <= 0 && (
                      <div className="mt-1 text-[10px] text-slate-500 italic">
                        💡 Aucune économie pour l'instant — continue !
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 🚀 Hotfix #5 (Bug B) — Warning cross-team : heures saison > 0 mais 0 sur BT de l'équipe */}
          {bonusData?.cross_team_warning && (
            <Card className="border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="card-cross-team-warning">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-800 mb-1">⚠️ Attention — Mapping des heures</div>
                    <div className="text-sm text-amber-700">{bonusData.cross_team_message}</div>
                    <div className="text-xs text-amber-600 mt-2">
                      ➡️ Contacte un administrateur pour vérifier l'attribution de tes BT à ton chef d'équipe actuel.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 🆕 [v13.67 — 2026-02-28] REFONTE : Mes bons de travail cette saison
              Nouveau modèle « par BT » (spec utilisatrice) : les employés sont
              dispatchés sur différents contrats sans équipe permanente. Chaque BT
              montre la contribution personnelle : (budget − réel) × (mes heures / total).
              Ref : /app/memory/RCA_V13_67_BONUS_PAR_BT.md */}
          {Array.isArray(bonusData?.mes_bts) && bonusData.mes_bts.length > 0 && (
            <MesBonsTravailSaison mesBts={bonusData.mes_bts} data-testid="section-mes-bons-travail" />
          )}

          {/* 🔄 Phase A2.2 (2026-05-04) — Item #1 : ventilation cross-team par équipe
              📌 [v13.67 — 2026-02-28] Marqué LEGACY : la vue « par équipe » ne sert plus
              au calcul du bonus (calcul désormais par BT). Conservée à titre informatif,
              repliable et clairement étiquetée. */}
          {Array.isArray(bonusData?.mes_equipes) && bonusData.mes_equipes.length >= 2 && (
            <details className="group" data-testid="mes-equipes-legacy-details">
              <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 px-1 py-1 select-none">
                <ChevronRight className="w-3 h-3 group-open:hidden" />
                <ChevronDown className="w-3 h-3 hidden group-open:inline" />
                Voir mes équipes cette saison (vue équipe historique · à titre informatif)
              </summary>
              <Card className="border-blue-200 bg-gradient-to-br from-blue-50/70 to-indigo-50/70 mt-2" data-testid="card-mes-equipes">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-blue-900 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  Mes équipes cette saison
                  <Badge variant="outline" className="ml-1 text-xs">
                    {bonusData.mes_equipes.length} équipe{bonusData.mes_equipes.length > 1 ? 's' : ''}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-300 ml-1">
                    Vue équipe (v13.66)
                  </Badge>
                </CardTitle>
                <div className="text-[11px] text-slate-500 mt-1">
                  ℹ️ Cette section est conservée à titre informatif. Depuis la refonte v13.67, le bonus est calculé par bon de travail (voir la section « Mes bons de travail cette saison » ci-dessus).
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-2" data-testid="mes-equipes-list">
                  {bonusData.mes_equipes.map((eq) => {
                    const palierEqInfo = eq.palier && eq.palier.niveau ? PALIER_CONFIG[eq.palier.niveau] : null;
                    const PALIER_ORDER = ["Bronze", "Argent", "Or", "Platine"];
                    const PALIER_RANGES = {
                      Bronze: "0 à 50h économisées",
                      Argent: "50 à 100h économisées",
                      Or: "100 à 150h économisées",
                      Platine: "150h et plus",
                    };
                    const niveau = eq.palier?.niveau;
                    const progression = Math.max(0, Math.min(100, Number(eq.palier?.progression) || 0));
                    const idx = niveau ? PALIER_ORDER.indexOf(niveau) : -1;
                    const palierSuivant = idx >= 0 && idx < PALIER_ORDER.length - 1 ? PALIER_ORDER[idx + 1] : null;
                    const palierAtteint = niveau === "Platine";
                    // 🆕 2026-05-23 — Heures restantes pour atteindre le prochain palier.
                    // Chaque tranche = 50h. Si progression = 75% dans Bronze → 12,5h restantes.
                    const heuresRestantes = palierSuivant ? (1 - progression / 100) * 50 : 0;
                    // Couleur de la barre selon le palier actuel (sans révéler de %)
                    const progressBarColor = (
                      niveau === "Platine" ? "bg-purple-500" :
                      niveau === "Or" ? "bg-amber-500" :
                      niveau === "Argent" ? "bg-slate-400" :
                      niveau === "Bronze" ? "bg-orange-500" :
                      "bg-slate-300"
                    );
                    return (
                    <div
                      key={eq.chef_equipe_id}
                      className="bg-white/60 border border-blue-100 rounded px-3 py-2 text-sm space-y-1.5"
                      data-testid={`mes-equipes-row-${eq.chef_equipe_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-slate-800 truncate">
                            {eq.role_dans_equipe === "chef" ? "👑 " : ""}{eq.chef_nom}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {eq.role_dans_equipe === "chef" ? "Chef" : "Peintre"}
                          </Badge>
                          {palierEqInfo && niveau !== "Aucun" && (
                            <Badge
                              variant="outline"
                              className={`text-xs ${palierEqInfo.bg} ${palierEqInfo.color} border-current`}
                              data-testid={`palier-equipe-${eq.chef_equipe_id}`}
                              title={`Palier ${niveau} : ${PALIER_RANGES[niveau] || ''}. Les paliers sont par tranches de 50h économisées (équipe).`}
                            >
                              <palierEqInfo.icon className="w-3 h-3 mr-0.5" />Équipe {niveau}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-500">
                            {eq.heures?.toFixed(2).replace('.', ',')}h
                          </span>
                          <span className={`font-medium ${eq.heures_economisees_perso > 0 ? 'text-emerald-600' : eq.heures_economisees_perso < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                            {eq.heures_economisees_perso > 0 ? '+' : ''}{(eq.heures_economisees_perso || 0).toFixed(2).replace('.', ',')}h éco
                          </span>
                          <span className="font-bold text-blue-700 min-w-[70px] text-right">
                            {formatCAD(eq.bonus_part)}
                          </span>
                        </div>
                      </div>
                      {/* 🆕 2026-05-23 — Progression vers palier suivant (style Strava). Aucun % de
                          redistribution n'est dévoilé : seul le % de progression dans le palier actuel.
                          🆕 2026-05-23 (suite) — Affiche aussi les heures restantes pour atteindre le
                          prochain palier (paliers de 50h, info accessible aux peintres). */}
                      {niveau && niveau !== "Aucun" && (
                        <div
                          className="flex items-center gap-2 text-[11px] text-slate-600"
                          data-testid={`progression-palier-${eq.chef_equipe_id}`}
                          title={`Les paliers sont par tranches de 50h économisées par l'équipe. Actuellement : ${niveau} (${PALIER_RANGES[niveau] || ''}).`}
                        >
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${progressBarColor} transition-all duration-700 ease-out`}
                              style={{ width: `${progression}%` }}
                            />
                          </div>
                          <span className="shrink-0 tabular-nums">
                            {palierAtteint
                              ? <span className="text-purple-700 font-medium">🏆 Palier max atteint !</span>
                              : (<>
                                  <span className="font-medium">{progression.toFixed(0)}%</span>
                                  {" vers "}
                                  <span className="font-medium">{palierSuivant}</span>
                                  <span className="text-slate-400 ml-1">
                                    · plus que {heuresRestantes.toFixed(1).replace('.', ',')}h
                                  </span>
                                </>)
                            }
                          </span>
                        </div>
                      )}
                    </div>
                    );
                  })}
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-blue-100 text-sm">
                    <span className="text-slate-500">Total :</span>
                    <span className="font-bold text-blue-800">{formatCAD(bonusData.bonus_estime)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            </details>
          )}

          {/* Si une seule équipe → mention discrète sous le bloc principal (testid pour QA) */}
          {Array.isArray(bonusData?.mes_equipes) && bonusData.mes_equipes.length === 1 && (
            <div className="text-xs text-slate-500 -mt-2" data-testid="mes-equipes-single">
              Équipe (v13.66 · à titre informatif) : <span className="font-medium text-slate-700">{bonusData.mes_equipes[0].chef_nom}</span> ({bonusData.mes_equipes[0].role_dans_equipe})
            </div>
          )}

          {/* Bannière Éligibilité + Bonus(es) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Éligibilité globale */}
            {(() => {
              const saisonEnCours = isSaisonEnCours(saison);
              const heuresEcoNeg = (bonusData?.heures_economisees_perso ?? 0) < 0;
              const elig = getEligibilityStatus({
                eligible: !!perfData.eligible,
                bonusEstime: bonusData?.bonus_estime ?? 0,
                saisonEnCours,
                heuresEcoNeg,
              });
              return (
                <Card className={`border-2 ${elig.border} ${elig.bg}`}>
                  <CardContent className="pt-5">
                    <div className="flex items-center gap-3 mb-2">
                      {perfData.eligible
                        ? <CheckCircle className="w-8 h-8 text-emerald-500" />
                        : (saisonEnCours
                            ? <Clock className="w-8 h-8 text-amber-500" />
                            : <XCircle className="w-8 h-8 text-red-400" />)
                      }
                      <div>
                        <div className={`text-lg font-bold ${elig.color}`}>{elig.label}</div>
                        <div className="text-sm text-slate-500">{perfData.employee_nom} — {perfData.role || (isChef ? "Chef d'équipe" : "Employé")}</div>
                        <div className="text-xs text-slate-500 italic mt-0.5">{elig.sub}</div>
                      </div>
                    </div>
                    {!perfData.eligible && perfData.raisons_ineligibilite?.length > 0 && (
                      <ul className={`text-sm mt-2 space-y-0.5 ml-11 ${saisonEnCours ? 'text-amber-700' : 'text-red-600'}`}>
                        {perfData.raisons_ineligibilite.map((r, i) => <li key={i}>• {r}</li>)}
                      </ul>
                    )}
                {/* 🔄 Phase A2.3 — Clients responsables des critères CP en échec */}
                {!perfData.eligible && perfData.criteres_cp?.clients_qui_font_echouer?.length > 0 && (
                  <div className={`mt-3 ml-11 border-t pt-3 ${saisonEnCours ? 'border-amber-200' : 'border-red-200'}`} data-testid="cp-clients-fautifs">
                    <div className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${saisonEnCours ? 'text-amber-700' : 'text-red-700'}`}>
                      {saisonEnCours ? 'Dossiers à améliorer :' : 'Dossiers responsables :'}
                    </div>
                    {(() => {
                      // Regrouper par critere
                      const byCrit = {};
                      perfData.criteres_cp.clients_qui_font_echouer.forEach(f => {
                        const k = f.critere || 'autre';
                        if (!byCrit[k]) byCrit[k] = { label: f.label || k, items: [] };
                        byCrit[k].items.push(f);
                      });
                      return Object.entries(byCrit).map(([k, group]) => {
                        const detail = perfData.criteres_cp?.details_par_critere?.[k];
                        const score = detail?.score ?? '?';
                        const seuil = detail?.seuil ?? '?';
                        return (
                          <details key={k} className="mb-2 group" open>
                            <summary className={`cursor-pointer text-sm font-medium select-none ${saisonEnCours ? 'text-amber-700 hover:text-amber-800' : 'text-red-700 hover:text-red-800'}`}>
                              {saisonEnCours ? '🌱' : '❌'} {group.label} : {score}% &lt; {seuil}% requis ({group.items.length} dossier{group.items.length > 1 ? 's' : ''})
                            </summary>
                            <ul className="mt-1 ml-4 space-y-1 text-xs text-slate-700">
                              {group.items.slice(0, 8).map((f, i) => (
                                <li
                                  key={`${k}-${i}`}
                                  data-testid={`cp-clients-fautifs-row-${f.dossier_id || f.bon_id || i}`}
                                  className="flex flex-col"
                                >
                                  <span className="font-medium">
                                    {f.client_nom || f.numero_dossier || f.dossier_id || f.bon_id || '—'}
                                  </span>
                                  {f.details && (
                                    <span className="text-slate-500 italic">↳ {f.details}</span>
                                  )}
                                </li>
                              ))}
                              {group.items.length > 8 && (
                                <li className="italic text-slate-400">
                                  + {group.items.length - 8} autre{group.items.length - 8 > 1 ? 's' : ''}…
                                </li>
                              )}
                            </ul>
                          </details>
                        );
                      });
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
              );
            })()}

            {/* Bonus(es) */}
            <div className="space-y-3">
              {/* Bonus Variable (heures économisées) — tout le monde */}
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="text-xs text-blue-600 font-medium">
                          {isChef ? "Bonus variable (heures économisées)" : "Bonus estimé (brut)"}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 w-5 p-0 rounded-full"
                          onClick={() => setExplDialog(isCP ? 'bonus_variable_cp' : 'bonus_variable')}
                          title="Comprendre mon bonus potentiel"
                        >
                          <HelpCircle className="w-3.5 h-3.5 text-blue-400 hover:text-blue-600" />
                        </Button>
                      </div>
                      {/* 🆕 2026-02-21 Phase 1 — Toujours afficher le bonus potentiel.
                          Admissibilité communiquée séparément (couleur ambre si en cours, rouge si saison finie). */}
                      {(() => {
                        const saisonEnCours = isSaisonEnCours(saison);
                        const heuresEcoNeg = (bonusData?.heures_economisees_perso ?? 0) < 0;
                        const elig = getEligibilityStatus({
                          eligible: !!bonusData?.eligible,
                          bonusEstime: bonusData?.bonus_estime ?? 0,
                          saisonEnCours,
                          heuresEcoNeg,
                        });
                        return (
                          <>
                            <div className="text-3xl font-bold text-blue-800" data-testid="bonus-variable-montant">
                              {formatCAD(bonusData?.bonus_estime ?? 0)}
                            </div>
                            <div className="text-[11px] text-slate-500 -mt-0.5">Bonus potentiel estimé</div>
                            {palierInfo && (bonusData?.bonus_estime ?? 0) > 0 && !(Array.isArray(bonusData?.mes_equipes) && bonusData.mes_equipes.length >= 2) && (
                              <div
                                className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border text-xs font-medium ${palierInfo.bg} ${palierInfo.color} cursor-help`}
                                title={`Palier ${palierInfo.label} : ${
                                  palierInfo.label === 'Bronze' ? '0 à 50h' :
                                  palierInfo.label === 'Argent' ? '50 à 100h' :
                                  palierInfo.label === 'Or' ? '100 à 150h' :
                                  palierInfo.label === 'Platine' ? '150h et plus' : ''
                                } économisées par l'équipe. Les paliers sont par tranches de 50h.`}
                              >
                                <palierInfo.icon className="w-3 h-3" />Palier {palierInfo.label}
                              </div>
                            )}
                            {/* 🔧 2026-05-23 — Si multi-équipes : palier affiché PAR équipe (carte "Mes équipes cette saison")
                                pour éviter d'afficher un seul palier global qui serait trompeur.
                                Popover de transparence : décompose le bonus par équipe SANS révéler les % paliers (confidentiel). */}
                            {(bonusData?.bonus_estime ?? 0) > 0 && Array.isArray(bonusData?.mes_equipes) && bonusData.mes_equipes.length >= 2 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border text-xs font-medium bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100 transition-colors cursor-pointer"
                                    data-testid="multi-paliers-info-btn"
                                  >
                                    Multi-paliers
                                    <HelpCircle className="w-3 h-3" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 text-xs" data-testid="multi-paliers-popover">
                                  <div className="space-y-2">
                                    <div className="font-semibold text-slate-800 text-sm">
                                      Détail de mon bonus par équipe
                                    </div>
                                    <div className="text-[11px] text-slate-500 italic">
                                      Tu travailles dans {bonusData.mes_equipes.length} équipes cette saison. Chaque équipe a son propre palier de performance.
                                    </div>
                                    <div className="border-t border-slate-200 pt-2 space-y-1.5">
                                      {bonusData.mes_equipes.map((eq) => (
                                        <div key={eq.chef_equipe_id} className="flex items-center justify-between gap-2">
                                          <div className="flex items-center gap-1.5 min-w-0">
                                            <span className="truncate font-medium text-slate-700">{eq.chef_nom}</span>
                                            {eq.palier?.niveau && eq.palier.niveau !== "Aucun" && (
                                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                                {eq.palier.niveau}
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 text-[11px] shrink-0">
                                            <span className={`${eq.heures_economisees_perso > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                                              {eq.heures_economisees_perso > 0 ? '+' : ''}{(eq.heures_economisees_perso || 0).toFixed(2).replace('.', ',')}h
                                            </span>
                                            <span className="font-semibold text-blue-700 min-w-[55px] text-right">
                                              {formatCAD(eq.bonus_part)}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="border-t border-slate-200 pt-2 flex items-center justify-between text-xs">
                                      <span className="text-slate-600">Bonus total estimé</span>
                                      <span className="font-bold text-blue-800">{formatCAD(bonusData.bonus_estime)}</span>
                                    </div>
                                    <div className="text-[10px] text-slate-400 italic">
                                      Les paliers Bronze, Argent, Or et Platine sont franchis par tranches de <span className="font-semibold">50 heures économisées</span> par l'équipe. Plus l'équipe en économise, plus le palier monte et plus la part redistribuée est généreuse.
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                            {(bonusData?.taux_par_heure_economisee != null) && (bonusData?.bonus_estime ?? 0) > 0 && (
                              <div className="mt-1 text-xs text-blue-700" data-testid="taux-par-heure-economisee">
                                <span className="font-semibold">{formatCAD(bonusData.taux_par_heure_economisee)}/h</span>
                                <span className="text-blue-500"> par heure économisée</span>
                              </div>
                            )}
                            {/* Statut admissibilité (séparé du montant) */}
                            <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${elig.bg} ${elig.border} ${elig.color}`}
                              data-testid="statut-admissibilite">
                              {elig.label}
                            </div>
                            <div className="text-[10px] text-slate-500 italic mt-1 max-w-md">
                              {elig.sub}
                            </div>
                            {/* 🆕 [v13.66 Volet A — 2026-02-28] Bandeau explicatif « Pourquoi 0 $ »
                                Consomme `bonus_zero_explication` retourné par le backend quand
                                `bonus_estime === 0`. Explique clairement à l'utilisateur pourquoi
                                son bonus est à zéro alors qu'il a peut-être un écart global positif.
                                Ref : /app/memory/RCA_V13_66_BONUS_ZERO.md */}
                            {(bonusData?.bonus_estime ?? 0) === 0 && bonusData?.bonus_zero_explication && (() => {
                              const expl = bonusData.bonus_zero_explication;
                              const raison = expl.raison || "autre";
                              // Palette par raison (v13.66 + adaptations v13.67)
                              const styleByRaison = {
                                inadmissible:               { bg: "bg-red-50",    border: "border-red-300",    text: "text-red-800",    icon: "🚫" },
                                aucune_activite:            { bg: "bg-slate-50",  border: "border-slate-300",  text: "text-slate-700",  icon: "💤" },
                                aucune_equipe:              { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-800",  icon: "🧭" },
                                aucun_bt_admissible:        { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-800",  icon: "📋" },
                                equipe_deficitaire:         { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-800",  icon: "🤝" },
                                contributions_bt_negatives: { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-800",  icon: "⚖️" },
                                aucune_heure_economisee:    { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-800",  icon: "⏳" },
                                autre:                      { bg: "bg-slate-50",  border: "border-slate-300",  text: "text-slate-700",  icon: "ℹ️" },
                              };
                              const s = styleByRaison[raison] || styleByRaison.autre;
                              const equipesDefic = Array.isArray(expl.equipes_deficitaires) ? expl.equipes_deficitaires : [];
                              // 🆕 [v13.67] Nouveau champ : liste des BT en dépassement
                              const btsDefic = Array.isArray(expl.bts_deficitaires) ? expl.bts_deficitaires : [];
                              return (
                                <div
                                  className={`mt-2 p-3 rounded-md border ${s.bg} ${s.border} ${s.text} max-w-md`}
                                  data-testid="bonus-zero-explication-badge"
                                  data-raison={raison}
                                >
                                  <div
                                    className="text-xs font-semibold mb-1 flex items-start gap-2"
                                    data-testid={`bonus-zero-raison-${raison}`}
                                  >
                                    <span aria-hidden="true">{s.icon}</span>
                                    <span>Pourquoi ce montant est à 0 $ ?</span>
                                  </div>
                                  <div className="text-[11px] leading-snug" data-testid="bonus-zero-details">
                                    {expl.details}
                                  </div>
                                  {equipesDefic.length > 0 && (
                                    <ul
                                      className="mt-2 text-[11px] space-y-0.5 list-disc list-inside"
                                      data-testid="bonus-zero-equipes-deficitaires"
                                    >
                                      {equipesDefic.slice(0, 5).map((eq, i) => (
                                        <li key={`${eq.chef_nom}-${i}`} className="opacity-90">
                                          Équipe de <span className="font-semibold">{eq.chef_nom}</span>
                                          {typeof eq.heures_perso === 'number' && (
                                            <> — vous y avez fait {eq.heures_perso.toFixed(2).replace('.', ',')} h</>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {/* 🆕 [v13.67] Détail BT en dépassement (compensent les BT sous-budget) */}
                                  {btsDefic.length > 0 && (
                                    <ul
                                      className="mt-2 text-[11px] space-y-0.5 list-disc list-inside"
                                      data-testid="bonus-zero-bts-deficitaires"
                                    >
                                      {btsDefic.slice(0, 5).map((bt, i) => (
                                        <li key={`${bt.bt_id || bt.numero_bt || i}`} className="opacity-90">
                                          BT <span className="font-semibold font-mono">{bt.numero_bt || bt.bt_id?.slice(0, 8)}</span>
                                          {bt.client_nom && <> ({bt.client_nom})</>}
                                          {typeof bt.contribution_bt === 'number' && (
                                            <> — contribution {bt.contribution_bt > 0 ? '+' : ''}{bt.contribution_bt.toFixed(2).replace('.', ',')} h</>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Filet historique : bonus 0 $ sans explication backend (backward compat) */}
                            {(bonusData?.bonus_estime ?? 0) === 0 && !bonusData?.bonus_zero_explication && saisonEnCours && (
                              <div className="text-[10px] text-slate-400 italic mt-1 max-w-md">
                                💡 Aucune heure économisée pour l'instant. Le montant peut évoluer pendant la saison.
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {isChef && <TrendingUp className="w-8 h-8 text-blue-400" />}
                  </div>
                </CardContent>
              </Card>

              {/* Bonus Fixe — Chefs seulement */}
              {isChef && (() => {
                const saisonEnCours = isSaisonEnCours(saison);
                const eligibleFixe = !!bonusFixeData?.eligible_bonus_fixe;
                const montantFixe = bonusFixeData?.montant_fixe || 0;
                const montantConfig = bonusFixeData?.montant_configure || bonusFixeData?.montant_par_saison || 0;
                const montantAffiche = eligibleFixe ? montantFixe : montantConfig;
                return (
                <Card className={`border-2 ${eligibleFixe ? 'border-purple-400 bg-purple-50' : (saisonEnCours ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-slate-50')}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-xs text-purple-600 font-medium mb-0.5">Bonus fixe (critères de performance)</div>
                        <div className={`text-3xl font-bold ${eligibleFixe ? 'text-purple-800' : 'text-slate-700'}`}>
                          {montantAffiche > 0 ? formatCAD(montantAffiche) : 'à confirmer'}
                        </div>
                        <div className="text-[11px] text-slate-500 -mt-0.5">
                          {eligibleFixe ? 'Bonus fixe versable' : 'Bonus fixe potentiel'}
                        </div>
                        {!eligibleFixe && (
                          <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${saisonEnCours ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-red-50 border-red-300 text-red-700'}`}>
                            {saisonEnCours ? '⏳ Versement à confirmer' : '🚫 Non admissible cette saison'}
                          </div>
                        )}
                        <div className="text-[10px] text-slate-500 italic mt-1">
                          {saisonEnCours
                            ? 'Versement confirmé en fin de saison selon les critères de performance.'
                            : 'Versement basé sur les critères de performance de la saison.'}
                        </div>
                        {bonusFixeData && !eligibleFixe && bonusFixeData.raisons_ineligibilite?.length > 0 && (
                          <ul className={`text-xs mt-1.5 space-y-0.5 ${saisonEnCours ? 'text-amber-700' : 'text-red-600'}`}>
                            {bonusFixeData.raisons_ineligibilite.slice(0, 2).map((r, i) => <li key={i}>• {r}</li>)}
                          </ul>
                        )}
                      </div>
                      <div className="text-right">
                        {isProprietaire && editingMontant ? (
                          <div className="flex gap-1 items-center">
                            <input
                              type="number"
                              className="w-24 border rounded px-2 py-1 text-sm"
                              value={newMontant}
                              onChange={e => setNewMontant(e.target.value)}
                            />
                            <Button size="sm" className="text-xs px-2 py-1 h-auto" onClick={saveMontantFixe}>OK</Button>
                            <Button size="sm" variant="ghost" className="text-xs px-2 py-1 h-auto" onClick={() => setEditingMontant(false)}>X</Button>
                          </div>
                        ) : isProprietaire ? (
                          <Button size="sm" variant="ghost" className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setEditingMontant(true)}>
                            Modifier le montant
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })()}
            </div>
          </div>

          <Tabs defaultValue="criteres">
            <TabsList>
              <TabsTrigger value="criteres">Critères d'admissibilité</TabsTrigger>
              {bonusData?.equipe && <TabsTrigger value="equipe">Performance</TabsTrigger>}
            </TabsList>

            {/* ─── Onglet Critères ─────────────────────────────────────────── */}
            <TabsContent value="criteres" className="mt-4 space-y-3">

              {/* ── Critères TERRAIN (Chef / Peintre) ─────────────────────── */}
              {!isCP && (
                <>
                  {/* 1. Heures travaillées */}
                  {(() => {
                    const projection = computeProjectionHeures(saison, perfData.heures?.total, perfData.heures?.seuil);
                    return (
                  <Card className={`border-l-4 ${perfData.heures?.ok ? 'border-l-emerald-400' : (saisonEnCours ? 'border-l-amber-400' : 'border-l-red-400')}`}>
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${perfData.heures?.ok ? 'bg-emerald-100' : (saisonEnCours ? 'bg-amber-100' : 'bg-red-100')}`}>
                            <Clock className={`w-5 h-5 ${perfData.heures?.ok ? 'text-emerald-600' : (saisonEnCours ? 'text-amber-600' : 'text-red-500')}`} />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-slate-800 text-sm">Heures totales travaillées dans la saison</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              Toutes les heures poinçonnées (BT, BT internes, formation, etc.). Sert UNIQUEMENT au seuil d'admissibilité.
                            </div>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className={`text-2xl font-bold ${perfData.heures?.ok ? 'text-emerald-600' : (saisonEnCours ? 'text-amber-600' : 'text-red-500')}`}>
                                {perfData.heures?.total?.toFixed(2).replace('.', ',')}h
                              </span>
                              <span className="text-sm text-slate-400">/ {perfData.heures?.seuil}h requis</span>
                              {!perfData.heures?.ok && saisonEnCours && (
                                <span className="text-xs text-amber-700">🌱 En progression — {(perfData.heures?.seuil - perfData.heures?.total)?.toFixed(2).replace('.', ',')}h restantes</span>
                              )}
                              {!perfData.heures?.ok && !saisonEnCours && (
                                <span className="text-xs text-red-500">
                                  Il manquait {(perfData.heures?.seuil - perfData.heures?.total)?.toFixed(2).replace('.', ',')}h
                                </span>
                              )}
                            </div>
                            <Progress value={Math.min(100, perfData.heures?.total / perfData.heures?.seuil * 100)} className="h-1.5 mt-1.5" />
                            {/* 🆕 2026-02-21 Phase 4 — Projection linéaire de saison */}
                            {projection && (
                              <div
                                className={`mt-2 text-xs rounded-md px-2 py-1.5 border ${
                                  projection.ton === 'bonne_voie'
                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                    : 'bg-amber-50 border-amber-200 text-amber-800'
                                }`}
                                data-testid="projection-heures-saison"
                              >
                                <span className="font-medium">
                                  {projection.ton === 'bonne_voie' ? '🚀 ' : '📈 '}
                                </span>
                                {projection.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-[11px] px-2 py-1 h-auto gap-1 text-blue-600 hover:bg-blue-50" onClick={() => setExplDialog('heures_minimum')} title="Comprendre ce critère">
                          <HelpCircle className="w-3.5 h-3.5" /> Comprendre
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                    );
                  })()}
                </>
              )}

              {/* 2. Avertissements (filtré par rôle côté backend) */}
              <Card className={`border-l-4 ${avertOk ? 'border-l-emerald-400' : (saisonEnCours ? 'border-l-amber-400' : 'border-l-red-400')}`}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${avertOk ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        <AlertTriangle className={`w-5 h-5 ${avertOk ? 'text-emerald-600' : 'text-red-500'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800 text-sm">Avertissements</span>
                          {avertOk
                            ? <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">Tout OK</Badge>
                            : <Badge variant="destructive" className="text-xs">Seuil atteint</Badge>
                          }
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {Object.entries(perfData.avertissements?.par_type || {}).map(([type, info]) => (
                            <div key={type} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${!info.ok ? 'bg-red-50 border-red-300 text-red-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                              {info.label}: <strong className="ml-0.5">{info.count}/{info.seuil}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => setExplDialog(isCP ? 'cp_avertissements' : 'avertissements')} title="En savoir plus">
                        <HelpCircle className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDetailDialog({ key: 'avertissements', data: perfData.avertissements })} title="Voir détails">
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ── Critères communs (Terrain / Chef seulement) ──────────── */}
              {!isCP && (
                <>
                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Temps"
                    score={perfData.temps?.score}
                    seuil={perfData.temps?.seuil}
                    ok={perfData.temps?.ok}
                    totalEvals={perfData.temps?.total}
                    icon={Clock}
                    onExplain={() => setExplDialog('temps')}
                    onDetails={() => setDetailDialog({ key: 'temps', data: perfData.temps })}
                  />

                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Service après-travaux"
                    score={perfData.service_call?.score}
                    seuil={perfData.service_call?.seuil}
                    ok={perfData.service_call?.ok}
                    totalEvals={perfData.service_call?.total}
                    icon={Wrench}
                    onExplain={() => setExplDialog('service_call')}
                    onDetails={() => setDetailDialog({ key: 'service_call', data: perfData.service_call })}
                  />

                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Gestion du Contrat"
                    score={perfData.gestion_contrat?.score}
                    seuil={perfData.gestion_contrat?.seuil}
                    ok={perfData.gestion_contrat?.ok}
                    totalEvals={perfData.gestion_contrat?.total}
                    icon={ClipboardCheck}
                    onExplain={() => setExplDialog('gestion_contrat')}
                    onDetails={() => setDetailDialog({ key: 'gestion_contrat', data: perfData.gestion_contrat })}
                    extra={!isChef && <Badge variant="outline" className="text-xs">Critères tout le monde seulement</Badge>}
                  />

                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Propreté des unités"
                    score={perfData.proprete?.score}
                    seuil={perfData.proprete?.seuil}
                    ok={perfData.proprete?.ok}
                    totalEvals={perfData.proprete?.total}
                    icon={Truck}
                    onExplain={() => setExplDialog('proprete')}
                    onDetails={() => setDetailDialog({ key: 'proprete', data: perfData.proprete })}
                  />

                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Inspection de chantier"
                    score={perfData.inspection?.score}
                    seuil={perfData.inspection?.seuil}
                    ok={perfData.inspection?.ok}
                    totalEvals={perfData.inspection?.total}
                    icon={ClipboardCheck}
                    onExplain={() => setExplDialog('inspection')}
                    onDetails={() => setDetailDialog({ key: 'inspection', data: perfData.inspection })}
                  />
                </>
              )}

              {/* ── Critères spécifiques CP ────────────────────────────────── */}
              {isCP && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-purple-700 uppercase tracking-wide">Critères Chargée de Projet</span>
                    <Badge variant="outline" className="text-xs border-purple-300 text-purple-700">CP</Badge>
                  </div>

                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Objectif temps des équipes (80%)"
                    score={cpCriteres.objectif_temps?.score}
                    seuil={cpCriteres.objectif_temps?.seuil || 80}
                    ok={cpCriteres.objectif_temps?.ok}
                    totalEvals={cpCriteres.objectif_temps?.total}
                    icon={Clock}
                    onExplain={() => setExplDialog('cp_objectif_temps')}
                    onDetails={() => setCpDetailDialog({ critereKey: 'objectif_temps', critereData: cpCriteres.objectif_temps })}
                  />

                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Photos avant/après (100%)"
                    score={cpCriteres.photos?.score}
                    seuil={100}
                    ok={cpCriteres.photos?.ok}
                    totalEvals={cpCriteres.photos?.total}
                    icon={ClipboardCheck}
                    onExplain={() => setExplDialog('cp_photos')}
                    onDetails={() => setCpDetailDialog({ critereKey: 'photos', critereData: cpCriteres.photos })}
                  />
                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Inspection (35% des bons)"
                    score={cpCriteres.inspection_cp?.score}
                    seuil={35}
                    ok={cpCriteres.inspection_cp?.ok}
                    totalEvals={cpCriteres.inspection_cp?.total}
                    icon={ClipboardCheck}
                    onExplain={() => setExplDialog('cp_inspection')}
                    onDetails={() => setCpDetailDialog({ critereKey: 'inspection_cp', critereData: cpCriteres.inspection_cp })}
                  />
                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Bons de travail complets (100%)"
                    score={cpCriteres.completude_bt?.score}
                    seuil={100}
                    ok={cpCriteres.completude_bt?.ok}
                    totalEvals={cpCriteres.completude_bt?.total}
                    icon={ClipboardCheck}
                    onExplain={() => setExplDialog('cp_completude_bt')}
                    onDetails={() => setCpDetailDialog({ critereKey: 'completude_bt', critereData: cpCriteres.completude_bt })}
                  />
                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Lavage pression 72h avant (90%)"
                    score={cpCriteres.lavage_72h?.score}
                    seuil={90}
                    ok={cpCriteres.lavage_72h?.ok}
                    totalEvals={cpCriteres.lavage_72h?.total}
                    icon={Truck}
                    onExplain={() => setExplDialog('cp_lavage_72h')}
                    onDetails={() => setCpDetailDialog({ critereKey: 'lavage_72h', critereData: cpCriteres.lavage_72h })}
                  />
                  <CritereCard saisonEnCours={saisonEnCours}
                    label="Courriel présentation (100%)"
                    score={cpCriteres.email_presentation?.score}
                    seuil={100}
                    ok={cpCriteres.email_presentation?.ok}
                    totalEvals={cpCriteres.email_presentation?.total}
                    icon={Wrench}
                    onExplain={() => setExplDialog('cp_email')}
                    onDetails={() => setCpDetailDialog({ critereKey: 'email_presentation', critereData: cpCriteres.email_presentation })}
                  />
                </div>
              )}

              {/* Lien vers Gestion Avertissements */}
              {!avertOk && (
                <div className="pt-2">
                  <Button variant="outline" size="sm" onClick={() => navigate('/gestion-avertissements')}>
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Voir la page Gestion des Avertissements
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* ─── Onglet Performance Équipe ────────────────────────────────── */}
            {bonusData?.equipe && (
              <TabsContent value="equipe" className="mt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  {[
                    { l: "Heures budgétées", v: `${bonusData.equipe.heures_budgetees?.toFixed(2).replace('.', ',')}h` },
                    { l: "Heures réelles", v: `${bonusData.equipe.heures_reelles?.toFixed(2).replace('.', ',')}h` },
                    { l: "Heures économisées", v: `${bonusData.equipe.heures_economisees > 0 ? '+' : ''}${bonusData.equipe.heures_economisees?.toFixed(2).replace('.', ',')}h`, highlight: bonusData.equipe.heures_economisees > 0 },
                    /* 🚀 P1 N7 (2026-05-04) — Carte "Valeur économisée" RETIRÉE
                       (violation spec §11 : "Aucune valeur économique globale visible aux employés"). */
                  ].map(({ l, v, highlight }) => (
                    <Card key={l}>
                      <CardContent className="pt-4">
                        <div className={`text-xl font-bold ${highlight ? 'text-emerald-600' : 'text-slate-800'}`}>{v}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {bonusData.equipe.palier && bonusData.equipe.palier.niveau !== "Aucun" && (
                  <Card className={`mb-4 ${palierInfo?.bg}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3">
                        {palierInfo && <palierInfo.icon className={`w-8 h-8 ${palierInfo.color}`} />}
                        <div className="flex-1">
                          <div className={`font-bold text-lg ${palierInfo?.color}`}>Palier {bonusData.equipe.palier.niveau}</div>
                          <Progress value={bonusData.equipe.palier.progression} className="h-2 mt-2" />
                          <div className="text-xs text-slate-500 mt-1">{bonusData.equipe.palier.progression?.toFixed(0)}% vers le palier suivant</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {bonusData.classement && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Classement de l'équipe</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="text-4xl font-bold text-slate-800">#{bonusData.classement.rang}</div>
                          <div className="text-xs text-slate-500">sur {bonusData.classement.total_equipes} équipes</div>
                        </div>
                        <div className="flex-1">
                          <Progress value={(1 - (bonusData.classement.rang - 1) / bonusData.classement.total_equipes) * 100} className="h-3" />
                        </div>
                        <div className={`text-xl font-bold ${bonusData.classement.ecart_moyenne > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {bonusData.classement.ecart_moyenne > 0 ? '+' : ''}{bonusData.classement.ecart_moyenne?.toFixed(2).replace('.', ',')}h vs moy.
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            )}
          </Tabs>
        </>
      )}

      {/* Grille info critères (toujours visible) — masquée en vue globale (v13.66 Volet B) */}
      {!perfData && !loading && vue !== 'global' && (
        <Card className="bg-slate-50">
          <CardHeader><CardTitle className="text-base">Critères d'admissibilité</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                { label: "Heures minimum", value: "600h travaillées", scope: "terrain", desc: "Équivaut à travailler à temps plein pendant toute une saison." },
                { label: "Temps", value: "≥ 80%", scope: "terrain" },
                { label: "Service Call", value: "≥ 85%", scope: "terrain" },
                { label: "Gestion contrat", value: "≥ 80%", scope: "terrain" },
                { label: "Propreté", value: "≥ 80%", scope: "terrain" },
                { label: "Inspection chantier", value: "≥ 80%", scope: "terrain" },
                { label: "Objectif temps", value: "≥ 80%", scope: "cp" },
                { label: "Photos avant/après", value: "100%", scope: "cp" },
                { label: "Inspection CP", value: "≥ 35%", scope: "cp" },
                { label: "Bons complets", value: "100%", scope: "cp" },
                { label: "Lavage 72h", value: "≥ 90%", scope: "cp" },
                { label: "Courriel présentation", value: "100%", scope: "cp" },
              ].map(c => (
                <div key={c.label} className="bg-white rounded border border-slate-200 p-2">
                  <div className="text-slate-500 text-xs">{c.label}</div>
                  <div className="font-semibold text-slate-800">{c.value}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{c.scope === "cp" ? "CP" : "Chef / Peintre"}</div>
                  {c.desc && <div className="text-[10px] text-blue-500 mt-0.5">{c.desc}</div>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 🆕 [v13.66 Volet B] Critères d'admissibilité — panneau repliable en vue globale */}
      {vue === 'global' && (
        <Card className="bg-slate-50" data-testid="criteres-collapsible-card">
          <CardHeader className="pb-2">
            <button
              type="button"
              className="w-full flex items-center justify-between text-left"
              onClick={() => setCriteresSectionOpen(v => !v)}
              data-testid="btn-toggle-criteres"
            >
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-500" />
                Critères d'admissibilité (référence)
              </CardTitle>
              {criteresSectionOpen
                ? <ChevronDown className="w-4 h-4 text-slate-500" />
                : <ChevronRight className="w-4 h-4 text-slate-500" />
              }
            </button>
          </CardHeader>
          {criteresSectionOpen && (
            <CardContent data-testid="criteres-collapsible-content">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {[
                  { label: "Heures minimum", value: "600h travaillées", scope: "terrain", desc: "Équivaut à travailler à temps plein pendant toute une saison." },
                  { label: "Temps", value: "≥ 80%", scope: "terrain" },
                  { label: "Service Call", value: "≥ 85%", scope: "terrain" },
                  { label: "Gestion contrat", value: "≥ 80%", scope: "terrain" },
                  { label: "Propreté", value: "≥ 80%", scope: "terrain" },
                  { label: "Inspection chantier", value: "≥ 80%", scope: "terrain" },
                  { label: "Objectif temps", value: "≥ 80%", scope: "cp" },
                  { label: "Photos avant/après", value: "100%", scope: "cp" },
                  { label: "Inspection CP", value: "≥ 35%", scope: "cp" },
                  { label: "Bons complets", value: "100%", scope: "cp" },
                  { label: "Lavage 72h", value: "≥ 90%", scope: "cp" },
                  { label: "Courriel présentation", value: "100%", scope: "cp" },
                ].map(c => (
                  <div key={c.label} className="bg-white rounded border border-slate-200 p-2">
                    <div className="text-slate-500 text-xs">{c.label}</div>
                    <div className="font-semibold text-slate-800">{c.value}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{c.scope === "cp" ? "CP" : "Chef / Peintre"}</div>
                    {c.desc && <div className="text-[10px] text-blue-500 mt-0.5">{c.desc}</div>}
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Dialogs */}
      <ExplicationDialog
        open={!!explDialog}
        onClose={() => setExplDialog(null)}
        critere={explDialog}
      />
      <DetailsDialog
        open={!!detailDialog}
        onClose={() => setDetailDialog(null)}
        critere={detailDialog?.key}
        data={detailDialog?.data}
        isChef={isChef}
      />

      {/* 🆕 2026-02-21 Phase 8 — Modale générique critères CP cliquables */}
      <CpCritereDetailsDialog
        open={!!cpDetailDialog}
        onOpenChange={(open) => { if (!open) setCpDetailDialog(null); }}
        critereKey={cpDetailDialog?.critereKey}
        critereData={cpDetailDialog?.critereData}
      />

      {/* 🚀 P1 (2026-05-04) — Modal transparence : détails par BT (heures perso, budget, écart) */}
      <Dialog open={showDetailsBt} onOpenChange={setShowDetailsBt}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              {/* 🆕 2026-02-21 Phase 7 — Titre adapté CP */}
              {isCP
                ? `Détail des heures des équipes par BT — Vue chargée de projet — ${saison}`
                : `Détail de mes heures par BT — ${saison}`}
            </DialogTitle>
          </DialogHeader>
          {loadingDetailsBt && (
            <div className="text-center py-8 text-slate-500">Chargement…</div>
          )}
          {!loadingDetailsBt && detailsBt && (
            <div className="space-y-3">
              <div className="text-sm text-slate-700 bg-blue-50 rounded p-3 border border-blue-100 space-y-1">
                {detailsBt.is_cp ? (
                  <>
                    <p>📋 <strong>Vue Chargée de projet</strong> — Tu vois ici tous les BT des dossiers où tu es CP. Le détail des heures réelles vient de l'ensemble des punches sur ces BT (toutes équipes).</p>
                    <p className="text-[12px]">Les BT au statut <strong>« Travaux Terminés »</strong> et non « À l'heure » sont les seuls qui comptent pour ton bonus de saison.</p>
                  </>
                ) : (
                  <>
                    <p><strong>{detailsBt.total_perso?.toFixed(2).replace('.', ',')}h</strong> de travail perso sur <strong>{detailsBt.total_bts}</strong> BT{detailsBt.total_bts > 1 ? 's' : ''}.</p>
                    <p className="text-[12px]">Les BT au statut <strong>« Travaux Terminés »</strong> et non « À l'heure » sont les seuls comptés pour le bonus.</p>
                  </>
                )}
                <p className="text-[12px] text-slate-600">
                  💡 <strong>Lecture des colonnes :</strong> Budget BT = budget initial total du chantier · Réel BT = toutes les heures punchées par l'équipe sur ce BT · Écart BT = budget − réel (positif = économie de l'équipe).
                  {!detailsBt.is_cp && ' Ma part % = ta contribution en heures sur ce BT. Mes heures éco. estimées = écart × ta part (positif si économie, négatif si dépassement).'}
                </p>
              </div>
              {(detailsBt.details || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic">Aucun BT trouvé pour cette saison.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-600 text-xs">
                      <tr>
                        <th className="text-left p-2">Client</th>
                        <th className="text-left p-2">Statut</th>
                        <th className="text-right p-2" title="Heures budgétées totales du BT (initialement prévues sur le chantier).">Budget BT</th>
                        <th className="text-right p-2" title="Heures réelles totales du BT (toutes équipes confondues, depuis les punches).">Réel BT</th>
                        <th className="text-right p-2" title="Écart Budget BT − Réel BT (positif = économie de l'équipe, négatif = dépassement).">Écart BT</th>
                        {!detailsBt.is_cp && (
                          <>
                            <th className="text-right p-2" title="Ta part en % des heures réelles de ce BT.">Ma part %</th>
                            <th className="text-right p-2" title="Tes propres heures punchées sur ce BT.">Mes heures</th>
                            <th className="text-right p-2" title="Estimation de ta part personnelle de l'écart du BT (écart BT × ta part). Aide à visualiser ta contribution à l'économie ou au dépassement.">Mes h. éco. est.</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {detailsBt.details.map((d) => {
                        const ecartBt = d.ecart_bt_total ?? d.ecart ?? 0;
                        const ecartCls = ecartBt > 0 ? 'text-emerald-600' : ecartBt < 0 ? 'text-red-600' : 'text-slate-500';
                        const partPerso = d.heures_part_perso_estimee ?? 0;
                        const partPersoCls = partPerso > 0 ? 'text-emerald-600' : partPerso < 0 ? 'text-red-600' : 'text-slate-500';
                        return (
                          <tr
                            key={d.bt_id}
                            className={`border-t ${d.compte_dans_bonus ? 'bg-white' : 'bg-amber-50/40'}`}
                            data-testid={`row-bt-${d.bt_id}`}
                          >
                            <td className="p-2">
                              <div className="font-medium text-slate-800">{d.client_nom || '—'}</div>
                              <div className="text-[11px] text-slate-500">
                                {d.numero_bt || d.adresse_travaux || d.ville_travaux || d.titre_bt || ''}
                              </div>
                              {d.chef_equipe_nom && (
                                <div className="text-[10px] text-slate-400 italic">Chef : {d.chef_equipe_nom}</div>
                              )}
                            </td>
                            <td className="p-2">
                              {d.compte_dans_bonus ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] border border-emerald-200"
                                  data-testid={`badge-bt-status-${d.bt_id}`}
                                >
                                  ✓ Comptabilisé
                                </span>
                              ) : d.a_l_heure ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] border border-slate-200"
                                  title="BT facturé à l'heure : exclu du calcul du bonus (aucun budget d'heures à respecter)."
                                  data-testid={`badge-bt-status-${d.bt_id}`}
                                >
                                  ⏱ À l'heure
                                </span>
                              ) : d.budget_status === 'sans_budget' ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[11px] border border-rose-200"
                                  title="Aucun budget d'heures n'a été saisi sur ce BT. Ajoutez heures budgétées dans le bon de travail pour qu'il compte dans le bonus."
                                  data-testid={`badge-bt-status-${d.bt_id}`}
                                >
                                  ⨯ Sans budget
                                </span>
                              ) : d.budget_status === 'propose_non_approuve' ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[11px] border border-orange-200"
                                  title={`Budget de ${(d.heures_budgetees_proposees ?? 0).toFixed(2).replace('.', ',')}h proposé mais pas encore approuvé par le propriétaire. Il ne compte pas dans le bonus tant qu'il n'est pas approuvé.`}
                                  data-testid={`badge-bt-status-${d.bt_id}`}
                                >
                                  ⌛ Budget non approuvé
                                </span>
                              ) : d.statut === 'brouillon' ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] border border-amber-200"
                                  title="Bon de travail en brouillon (workflow CRM : le chef ne l'a pas encore activé). Ses heures ne comptent au bonus qu'après passage en 'En cours' puis 'Terminé'."
                                  data-testid={`badge-bt-status-${d.bt_id}`}
                                >
                                  📝 BT en brouillon
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] border border-amber-200"
                                  title="BT en cours. Il sera comptabilisé une fois marqué Terminé."
                                  data-testid={`badge-bt-status-${d.bt_id}`}
                                >
                                  ⚠ {d.statut || 'En cours'}
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right tabular-nums">
                              {d.a_l_heure
                                ? <span className="text-slate-400 italic" data-testid={`cell-budget-${d.bt_id}`}>Non applicable</span>
                                : d.budget_status === 'sans_budget'
                                  ? <span className="text-rose-400 italic" data-testid={`cell-budget-${d.bt_id}`}>—</span>
                                  : d.budget_status === 'propose_non_approuve'
                                    ? <span
                                        className="text-orange-500 italic"
                                        title={`Proposé : ${(d.heures_budgetees_proposees ?? 0).toFixed(2).replace('.', ',')}h — non approuvé`}
                                        data-testid={`cell-budget-${d.bt_id}`}
                                      >
                                        ({(d.heures_budgetees_proposees ?? 0).toFixed(2).replace('.', ',')}h en attente)
                                      </span>
                                    : <span data-testid={`cell-budget-${d.bt_id}`}>{`${(d.heures_budget_bt_total ?? 0).toFixed(2).replace('.', ',')}h`}</span>}
                            </td>
                            <td className="p-2 text-right tabular-nums">
                              {(d.heures_reelles_bt_total ?? 0).toFixed(2).replace('.', ',')}h
                            </td>
                            <td className={`p-2 text-right tabular-nums font-semibold ${d.a_l_heure ? 'text-slate-400' : ecartCls}`}>
                              {d.a_l_heure
                                ? <span className="italic font-normal">N/A</span>
                                : <>{ecartBt > 0 ? '+' : ''}{ecartBt.toFixed(2).replace('.', ',')}h</>}
                            </td>
                            {!detailsBt.is_cp && (
                              <>
                                <td className="p-2 text-right tabular-nums text-slate-600">
                                  {d.a_l_heure
                                    ? <span className="text-slate-400 italic">N/A</span>
                                    : `${(d.part_perso_pct ?? 0).toFixed(1).replace('.', ',')}%`}
                                </td>
                                <td className="p-2 text-right tabular-nums">
                                  {(d.heures_reelles_perso ?? 0).toFixed(2).replace('.', ',')}h
                                </td>
                                <td className={`p-2 text-right tabular-nums font-semibold ${d.a_l_heure ? 'text-slate-400' : partPersoCls}`} title="Estimation : écart BT × ta part. Aide à visualiser ta contribution à l'écart, pas le montant final qui dépend de paliers et de la répartition par rôle.">
                                  {d.a_l_heure
                                    ? <span className="italic font-normal text-amber-700">Exclu du bonus</span>
                                    : <>{partPerso > 0 ? '+' : ''}{partPerso.toFixed(2).replace('.', ',')}h</>}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonBonus;
